/**
 * @fileoverview Centralized entity ID generation utility
 * @package @puffin-core/id-generator
 * @copyright ParcelPuffin 2026
 *
 * EPI-001: Enterprise Prefixed ID Standard
 *
 * All entity IDs use type-prefixed format following the Stripe enterprise pattern:
 *   {3-char-prefix}_{uuidv4()} = 40 characters, stored as VARCHAR(40)
 *
 * This provides:
 * - Debuggability: instantly identify entity type from an ID
 * - Type safety: API endpoints can validate ID type at the boundary
 * - Self-documenting logs: "assigned usr_abc123 to loc_def456"
 * - Security: prevents ID confusion attacks (passing userId where locationId expected)
 *
 * Usage:
 *   const { generateId } = require('@puffin-core/id-generator');
 *   const locationId = generateId('location');  // "loc_550e8400-e29b-41d4-..."
 *   const userId = generateId('user');          // "usr_6ba7b810-9dad-11d1-..."
 */

'use strict';

// `uuid` v14 is ESM-ONLY (`"type": "module"`, no `require` condition). Plain Node 23 can still
// `require()` it, but jest under `--experimental-vm-modules` cannot and throws "Must use import to
// load ES Module" — which surfaces as this whole module being unloadable.
//
// That matters because most consumers of this file want CONSTANTS, not a mint:
// `@puffin-core/eslint-rule-core` reads `ID_PREFIXES` so lint rules stop hard-coding prefixes, and
// an ESLint rule has no business dragging a uuid runtime into the linter's process. Loading the
// generator on FIRST MINT instead of at import keeps the registry free to read anywhere.
// (Found via `@puffin-core/shared-config`, whose own suite runs with `--experimental-vm-modules`
// and which loads the web plugin to prove the i18n gate registers it.)
let uuidModule = null;
function uuidLib() {
  if (uuidModule === null) {
    uuidModule = require('uuid');
  }
  return uuidModule;
}
const uuidv4 = (...args) => uuidLib().v4(...args);
const uuidv5 = (...args) => uuidLib().v5(...args);

/**
 * Single source of truth for all entity ID prefixes.
 * Every prefix is exactly 3 lowercase characters.
 * Format: entityType → 3-char prefix
 *
 * To add a new entity type:
 * 1. Add the mapping here
 * 2. Create a migration to convert existing UUID columns to VARCHAR(40)
 * 3. Update the model to use generateId()
 * 4. See GUIDES/shared/standards/database/id-generation.md
 */
const ID_PREFIXES = {
  // Core entities
  location: 'loc',
  user: 'usr',
  role: 'rol',
  product: 'prd',
  customer: 'cst',
  order: 'ord',
  orderLineItem: 'oli',
  // MUTATION-INSTRUMENT-001 B96 (owner ruling Q-PO2-3, 2026-09-27) — one row per line RETURNED for
  // money (`order_line_item_returns`): which line, how many units (fractional allowed), and the
  // refund that paid for it. The ledger that refuses returning the same unit twice.
  orderLineItemReturn: 'olr',
  // VARIANT-STOCK-001 (owner rulings Q-V3 and Q-V2 / Q1, 2026-10-05) — the storefront's two stock records: what happened
  // to a sold line's STOCK on a refund or cancel (`order_item_stock_returns`), and a line a late payment could not
  // fill (`order_stock_shortfalls`). Both prefixes measured free before adoption.
  orderItemStockReturn: 'osr',
  orderStockShortfall: 'oss',
  // REGISTER-PRICER-AND-STOCK item 2 — the register's three stock records: a store that does NOT carry a product
  // (`product_location_not_carried`, owner ruling C3), a count a store owes (`stock_count_requests`: below zero, opening
  // count, carried over), and a register line's STOCK decision (`order_line_item_stock_returns`: a return, a void, a
  // cancel). All three prefixes measured free before adoption.
  productLocationNotCarried: 'pnc',
  stockCountRequest: 'scr',
  orderLineItemStockReturn: 'lsr',
  category: 'cat',
  // 🔴 DISTINCT from `category` above, deliberately. `cat` is the WEB storefront taxonomy
  // (`categories`: slug/SEO/image), referenced per channel by
  // `product_channel_listings.categoryId`. `pct` is the POS MERCHANDISING taxonomy
  // (`product_categories`) that `products.categoryId` points at — how staff browse at the
  // register. Owner ruling 2026-08-23: "Department = reporting, Category = merchandising";
  // both axes are real, so their ids must be distinguishable on sight.
  productCategory: 'pct',
  // OMNI-MEDIA-001 (2026-08-23). Product imagery is stored ONCE per product in
  // `product_media` (`pmd`), and a separate row in `product_media_channels` (`pmc`) decides
  // which assets are visible on which SALES CHANNEL and in what order — so a POS register
  // button image and a web hero image are the same library with different visibility, not
  // two copies of the same file. Owner ruling: "images should be saved together and then we
  // need a mechanism to manage which are visible in POS, which on the web".
  productMedia: 'pmd',
  productMediaChannel: 'pmc',
  lead: 'led',
  // Custom-design / quote (RFQ) flow (PurchasePuffin Phase 1D, 2026-06-08)
  quoteRequest: 'qot',
  quoteRequestItem: 'qli',
  quoteAttachment: 'qat',

  // Financial
  transaction: 'txn',
  // TRANSACTION-TAX-DETAILS-001: one row per (sale, tax rate) — the per-jurisdiction breakdown
  // behind `transactions.taxAmount`, which stores only the blended total. Append-only evidence:
  // a refund carries its own negative detail rows rather than editing these.
  transactionTaxDetail: 'ttd',
  // HELD-SALES-001 B1 — a HELD SALE (`saved_carts`): a register sale parked on one terminal and resumed on any
  // terminal at the store, and its lines (`saved_cart_items`). Minted by the server only; reserved by THINKPAD.
  savedCart: 'svd',
  savedCartItem: 'svi',
  // A3: one regulated-service record a held sale carries (`saved_cart_service_records`).
  savedCartServiceRecord: 'svs',
  payment: 'pay',
  invoice: 'inv',
  // OWNER-RULINGS-WALK-BUILDS L1 (owner ruling 27, "Build real invoices") — one row per billed line of
  // an invoice (`invoice_line_items`), written once inside the invoice's creating transaction and
  // priced by the register's ONE pricing engine. Its own prefix, never `inv`'s: a line is not an
  // invoice, and EPI-001 exists so an id names its entity on sight.
  invoiceLineItem: 'ili',
  complianceRecord: 'cpr',
  customerAccount: 'cac',
  // STORED-VALUE-001: the append-only movement row of `customer_accounts`. The
  // balance row (`cac`) and its ledger are separate entities precisely because
  // one is mutable state and the other is immutable evidence — sharing a prefix
  // would defeat the "identify the entity from the id" property EPI-001 exists for.
  accountTransaction: 'atx',
  // `fac` (financialAccount) and `rcn` (reconciliation) were retired with their pre-tenancy
  // `public.financial_accounts` / `public.reconciliations` tables (FULL-MERGE-1001 schema-origins,
  // owner-approved prune 2026-10-05, `20261005140100`); nothing mints them.
  refund: 'rfd',
  // PERMISSIONS-CANON-001 row 1d (U4): the pos record that a missed delivery guarantee's customer share was refunded on
  // an order (`guarantee_refunds`, pos `20261010199930`). `grf` measured free on 2026-10-30 across the registry's holders.
  guaranteeRefund: 'grf',
  discount: 'dsc',
  coupon: 'cpn',
  taxRate: 'txr',
  // STORED-VALUE-001 gift cards (ParcelPuffin). `gfc` rather than `gft`/`gcd`
  // because it reads unambiguously as "GiFt Card" next to `gct`
  // (Gift Card Transaction) — the pair is scanned together in logs.
  giftCard: 'gfc',
  giftCardTransaction: 'gct',

  // Operations
  terminal: 'trm',
  cashDrawer: 'cdr',
  // POS-PRINT-LAYOUTS-001 § 9 (2026-10-24): an ISSUED Z report, `store_day_reports` (pos 20261010197790).
  storeDayReport: 'sdr',
  // POS-PRINT-LAYOUTS-001 § 9 (owner ruling Z3): one denomination of a drawer count, `cash_drawer_count_denominations`.
  cashDrawerCountDenomination: 'ccd',
  session: 'ses',
  setting: 'set',

  // Team & Admin
  invitation: 'ivt',
  assignment: 'asg',
  subscription: 'sub',
  notification: 'ntf',
  // PALM-ENGAGE-001 walkthrough finding (2026-08-31): palm's quiet-hours deferral queue
  // (`palm_notification_queue.queueId`) minted with the UNREGISTERED type 'notifQueue', so
  // every queue insert threw at the generateId guard — the deferral store had never
  // accepted a row live.
  notificationQueue: 'ntq',
  goal: 'gol',
  schedule: 'sch',
  dashboard: 'dsh',
  // DCS-001 / Phase E3 (2026-04-27) — per-tenant dashboard group config rows
  // in `public.dashboard_group_configs`. Chain admins CRUD groups for the
  // dashboard customizer's PillSection taxonomy.
  dashboardGroupConfig: 'dgc',
  // T3 F10f (owner ruling RLT-Q9): the PLATFORM Console's dashboard groups — `public.platform_dashboard_group_configs`,
  // staff-authored; the tenant twin above is per company.
  platformDashboardGroupConfig: 'pdg',
  // USER-FACING-GEMS LEAD-UFG-A (owner ruling 2026-09-30, "a durable tool that allows admins like me to set the
  // prices and then connects to stripe"): the platform PLAN CATALOG — RETIRED by APP-ENTITLEMENT-001 Phase 13 (pos
  // 20261010172700 drops both tables; the prefixes stay registered because the platform audit chain's rows and the
  // applied seed migration carry ids of these shapes) — `public.platform_plans` (one row per
  // subscription tier, `ppl`) and `public.platform_plan_prices` (one row per tier × billing cycle, `ppp`, carrying
  // the amount and the Stripe Price it was published as). Both measured FREE before they were chosen.
  platformPlan: 'ppl',
  platformPlanPrice: 'ppp',
  // APP-ENTITLEMENT-001 (owner rulings 2026-10-10): the ONE app catalog and what an account holds —
  // `public.app_catalog` (one row per app, `app`), `public.app_catalog_dimensions` (its size dimensions and metered
  // consumables, `acd`), `public.app_catalog_prices` (the published unit prices, `acp`), `public.app_catalog_suggestions`
  // (the synergy pitches, `aps`); `public.tenant_app_entitlements` (one row per company × app, `tae`),
  // `public.tenant_app_entitlement_lines` (a quantity per dimension, `tal`), `public.tenant_app_recovery_shares`
  // (PatrolPuffin's share of each recovered refund, `trs`), `public.tenant_app_consumption_invoices` (the blocks of
  // a consumable one closed month consumed beyond its inclusion, settled once, `tci`),
  // `public.register_network_review_flags` (the soft multi-network flag staff review, `rnf`) and the tenant-schema
  // `terminal_network_observations` (a register's network fingerprint per heartbeat, `tnf`). All ten measured FREE
  // on main and on push 13's assembly (coordinator reservation 2026-10-14; `tci` measured 2026-10-14 on the same
  // trees, Phase 8) before they were chosen.
  appCatalogEntry: 'app',
  appCatalogDimension: 'acd',
  appCatalogPrice: 'acp',
  appCatalogSuggestion: 'aps',
  tenantAppEntitlement: 'tae',
  tenantAppEntitlementLine: 'tal',
  tenantAppRecoveryShare: 'trs',
  tenantAppConsumptionInvoice: 'tci',
  registerNetworkReviewFlag: 'rnf',
  terminalNetworkObservation: 'tnf',
  adminNotification: 'ann',
  workflow: 'wfl',
  task: 'tsk',

  // Checklists
  template: 'tpl',
  completion: 'cpl',
  item: 'itm',
  response: 'rsp',
  useCase: 'usc',
  // POS-PRINT-LAYOUTS-001 M1.E8B (owner ruling Q-E8B-1): one store's own checklist template for one use case
  // (`checklistUseCaseStoreAssignments`, pos `20261010198330`).
  checklistStoreAssignment: 'csa',

  // Inventory & Purchasing
  reservation: 'rsv',
  stockReservation: 'skr',
  adjustment: 'adj',
  transfer: 'tfr',
  purchaseOrder: 'por',
  purchaseOrderItem: 'poi',
  receiving: 'rcv',
  receivingTransactionItem: 'rci',
  supplier: 'spl',
  inventory: 'ivn',
  inventoryTransaction: 'ivx',
  productInventory: 'piv',

  // Shipping & Carriers
  shipment: 'shp',
  shipmentAddon: 'sha',
  shipmentInsurance: 'shn',
  // PLN-DEPT-PLAN-001 R3-A (2026-09-09): the append-only `shipment_events` timeline row a
  // status transition writes (its note lives there, not in a column `shipments` never had).
  shipmentEvent: 'she',
  // SHIPMENT-PACKAGES-001 (2026-10-04): one parcel of a shipment, a row of `shipment_packages`.
  // Every package is a row, package 1 included; `shipments` keeps package 1's number and box.
  shipmentPackage: 'spk',
  // POS-PRINT-LAYOUTS-001 M1.5a (2026-10-22, owner ruling "Track items per box"): how much of one sold goods line went
  // into one box, a row of `shipment_package_items` — what each box's packing slip lists.
  shipmentPackageItem: 'pki',
  // OWNER-RULINGS-2026-09-11 W3-BOOK (2026-09-11): a priced carrier quote held server-side for the
  // booking flow (`POST /shipping/shipments/quote` → `/book`), parked in the tenant cache. Since
  // POS-PRINT-LAYOUTS-001 M1.2 (owner rulings Q1-Q3, 2026-10-22) a quote the counter SAVES keeps the
  // same id as a `shipping_quotes` row, so a sale booked from it records the id it always had.
  shipmentQuote: 'sqt',
  // POS-PRINT-LAYOUTS-001 M1.4 (coordinator ruling 2026-10-24, the recorded issue): one ISSUED customer statement of
  // account, `statement_issues` (pos 20261010197370) — its number is the tenant's `statementNumberSeq`.
  statementIssue: 'sti',
  statementIssueDelivery: 'sid',
  // RESTORE-AND-SHIPPING-ENGINE-001 E2a (2026-10-10): the carriers' PUBLISHED reference data in pos-api
  // `public` (migration 20261006101110) — one dated, hashed VERSION of a carrier dataset (rate guide, fuel
  // table, surcharge schedule, DAS list, zone chart; platform- or store-loaded, owner ruling F3) …
  carrierPublishedDataset: 'cpd',
  // … and one surcharge row of a published surcharge schedule (the other row tables key on the dataset).
  carrierPublishedSurcharge: 'csr',
  // RESTORE-AND-SHIPPING-ENGINE-001 E3 (2026-10-11): one published RATE row. Its band depends on its basis
  // (`@puffin-core/shipping-pricing` RATE_BASES: weight break · per pound · packaging · cubic tier), so it keys on
  // its own id ("carrier guide rate"; `cpr` is complianceRecord).
  carrierPublishedRate: 'cgr',
  // … and a store's IMPORTED COST CARD (per tenant schema, migration 20261006101310): one immutable version of
  // one store's PostalMate wholesale card for one carrier, and one rate row of it ("cost card rate", `ccr`). 🔴 The
  // rate row's first registered prefix, `scr`, was free on its own base and NOT on the joined tree:
  // REGISTER-PRICER-AND-STOCK's `stockCountRequest` took it in a parallel lane (FULL-MERGE-1001 push 13). Re-picked
  // as `ccr`, measured free on the joined tree before any row was written.
  shippingCostCard: 'scc',
  shippingCostCardRate: 'ccr',
  // … and one to-the-cent VERIFICATION of a cost card (E5-6, 2026-10-12; migration 20261006101317): what the expiry
  // rule reads (owner rulings 19b and Q-RSE-17 rule 4b).
  shippingCostCardCheck: 'sck',
  // RESTORE-AND-SHIPPING-ENGINE-001 E6 (2026-10-10): a store's shipping MARGIN rule — how a carrier cost
  // (or published list) becomes the customer's price, company-wide or for one location (migration
  // 20261006101140, `shipping_margin_rules` per tenant schema).
  shippingMarginRule: 'smr',
  // RESTORE-AND-SHIPPING-ENGINE-001 E7 (2026-10-12): what a booked counter shipment was PRICED from (migration
  // 20261006101315, per tenant schema) — one append-only price BREAKDOWN per shipment, and one priced component LINE of
  // it (base, fuel, a surcharge …: its list, cost and price, the margin rule and the steps that made the price).
  shipmentPriceBreakdown: 'sbd',
  shipmentPriceLine: 'sbl',
  // RESTORE-AND-SHIPPING-ENGINE-001 E0 (2026-10-11, owner ruling F2): a store's OWN shipping rates, which the
  // storefront checkout quotes from (purchase migration 20261006101280, per tenant schema) — a ZONE of destinations,
  // one DESTINATION of it (a country, or one region), and a named RATE of the zone.
  storeShippingZone: 'ssz',
  storeShippingDestination: 'ssd',
  storeShippingRate: 'ssr',
  // LIFECYCLE-UPDATE-RULE-001 (2026-09-09), STATUS-HISTORY-001: the append-only
  // `print_job_status_history` row a print-job transition writes. The table has existed since 2024
  // with ZERO writers and ZERO rows in all eight `cmp_%` schemas — a history nobody was keeping.
  printJobStatusHistory: 'pjh',
  carrier: 'car',
  carrierConfig: 'ccf',
  carrierServiceConfig: 'csc',
  carrierZone: 'czn',
  carrierZoneMapping: 'czm',
  shippingAddonDef: 'sdf',
  shippingRate: 'srt',
  surcharge: 'src',
  fuelSurchargeRate: 'fsr',
  rateFormula: 'rfm',
  rateTable: 'rtb',
  insuranceClaim: 'icl',
  insuranceProvider: 'ipr',
  insuranceRate: 'irt',
  prohibitedItem: 'phi',

  // Customer Service
  dropOff: 'drp',
  arrival: 'arv',
  mailbox: 'mbx',
  mailboxRental: 'mbr',
  // MAILBOX-RENT-PAYMENTS-001 U2 — the rent money path: an attempt to pay a rental's rent (`mailbox_rent_payments`),
  // a fee assessed against it (`mailbox_rent_fees`), and the holder's autopay authorization
  // (`mailbox_autopay_authorizations`). Each measured free on 2026-10-24 before it was chosen.
  mailboxRentPayment: 'mrp',
  mailboxRentFee: 'mrf',
  autopayAuthorization: 'map',
  // MAILBOX-RENT-PAYMENTS-001 part 2 U8b-1 — a link a holder pays a rental's rent through (purchase-api's
  // `mailbox_payment_links`). `rpa`, measured free on 2026-11-02 (`mpl` / `mbp` were already held).
  mailboxPaymentLink: 'rpa',
  mailItem: 'mli',
  customerInteraction: 'cin',
  customerSegment: 'csg',

  // Platform & Config
  config: 'cfg',
  audit: 'aud',
  auditAnnotation: 'aan', // ALV-UX-001 T3: Activity Log annotations (append-only side table)
  device: 'dev',
  staff: 'stf',
  permission: 'prm',
  certificate: 'crt',
  platformAuditLog: 'pal',
  platformSetting: 'pls',
  systemSetting: 'ssy',
  systemSettingSnapshot: 'ssp', // UNREACHABLE-MODULE-BURNDOWN-001 Q-L13-6: settings restore points
  systemFeatureDefault: 'sfd',
  tenantFeatureConfig: 'tfc',
  tenantSecurity: 'tsc',
  fieldGroup: 'fgr',
  roleFieldPermission: 'rfp',
  permissionTemplate: 'pmt',

  // Notifications
  notificationPreference: 'npr',
  notificationTemplate: 'ntl',
  // A tenant's per-event notification settings row (NOTIFICATIONS-DISPATCHER-001 P2a).
  notificationEventSetting: 'nes',
  // A person's ONE notification preference row, shared by every app (NOTIFICATIONS-DISPATCHER-001 P3).
  userNotificationPreference: 'unp',

  // Supplementary
  comment: 'cmt',
  commentThread: 'thr',
  commentMention: 'mnt',
  commentEdit: 'ced',
  file: 'fil',
  sync: 'syn',

  // Skills & Proficiency (SKL-001)
  skillDefinition: 'skl',
  proficiencyLevel: 'plv',
  employeeSkill: 'esk',

  // Timekeeping
  punch: 'pch',
  break: 'brk',
  approval: 'apr',
  shiftToken: 'stk',
  breakLog: 'blg',
  breakRule: 'brl',
  breakType: 'btp',
  breakTypeLocation: 'btl',
  overtimeRule: 'ovr',
  // HTP-001 (2026-07-26) — timePunchAdjustment: a `time_punch_adjustments` row,
  // the correction vehicle for run-locked punches (M2). The paid punch stays
  // immutable; a signed-delta adjustment row is consumed by a future payroll
  // run. `tpa` verified collision-free against the full registry — distinct
  // from `tpl` (template), `tpt` (tenantPlanTier), `ptb` (portTempBridge),
  // and every other `tp*`/`pa*` neighbour (incl. the since-pruned `tap`
  // timecardApproval — HTP-001 retired its table and Q-51 removed the
  // orphaned registration); the
  // `should have no duplicate prefixes` guard test catches any regression.
  // (The HTP-001 plan text sketched `tpadj_`, but a 5-char prefix is
  // physically impossible: EPI-001 pins {3-char}_{uuid} = 40 chars and the
  // M2 column is VARCHAR(40) — `tpadj_` + uuid = 42 chars would be rejected
  // by PostgreSQL. `tpa` is the EPI-001-conformant registration.)
  timePunchAdjustment: 'tpa',

  // Printing
  printingJob: 'pjb',
  printingTemplate: 'ptp',
  // POS-PRINT-FILE-UPLOAD-001 Q-D3-M4 (2026-10-18) — `print_file_upload_sessions`: one RESUMABLE, CHUNKED print-file
  // upload in flight. A print-ready file larger than one request may carry (the edge's 100 MB body cap) arrives in
  // chunks under it, each idempotent by index; the completed session becomes one print job file.
  printFileUpload: 'pfu',
  // POS-PRINT-LAYOUTS-001 M1.8 (2026-10-22, owner ruling Q-RWO-10 "Both, thermal included (Recommended)"): a store's
  // printer (`printers`: its station or none, the ONE language it is sent raw, its dpi and media) and which printer
  // prints what (`printer_assignments`: store, station or store-wide, purpose → printer).
  printer: 'prn',
  printerAssignment: 'pta',
  // POS-PRINT-LAYOUTS-001 M1.9: a store's paired print agent (`print_agents`) and one print request in its queue
  // (`print_queue_jobs`).
  printAgent: 'pra',
  printQueueJob: 'pqj',

  // Regulated counter services (UNREACHABLE-MODULE-BURNDOWN-001 Q-L13b-3): the compliance record a
  // passport-photo sale or a notarial act leaves behind.
  passportPhotoSession: 'pph',
  notaryJournalEntry: 'nje',

  // Marketing. There is no `campaign` key: `cmp` is the TENANT prefix (TENANT-ID-001), and the campaign entity is
  // `marketingCampaign` (campaign-prefix C2, owner ruling 2026-10-21).
  marketingCampaign: 'mcm',
  campaignMessage: 'cmm',

  // Dropdown System
  dropdownType: 'dty',
  dropdownOption: 'dop',
  dropdownCustomOption: 'dco',
  dropdownOverride: 'dov',

  // Authentication & Security
  passkey: 'pky',
  attempt: 'att',
  // EPHEMERAL (EPHEMERAL_ID_TYPES): a passkey / MFA challenge held in Redis for minutes, never a database row.
  challengesession: 'chs',
  code: 'cod',
  alert: 'alt',
  incident: 'inc',
  correlation: 'cor',
  sessionDeviceHistory: 'sdh',
  pinLockout: 'plk',
  passwordHistory: 'phx',

  // Integration & External
  integration: 'itg',
  oauthToken: 'otk',
  oauthAuditLog: 'oal',
  quickBooksConnection: 'qbc',
  quickBooksAccountMapping: 'qam',
  quickBooksStoreMapping: 'qsm',

  // Analytics (general)
  event: 'evt',
  plan: 'pln',
  visitorutm: 'vum',

  // Analytics Schema Entities
  analyticsTenant: 'atn',
  analyticsConfig: 'acf',
  analyticsApiKey: 'aak',
  analyticsVisitor: 'avs',
  analyticsSession: 'asn',
  analyticsEvent: 'aev',
  analyticsGoal: 'agl',
  analyticsFunnel: 'afn',
  analyticsFunnelStep: 'afs',
  analyticsConversion: 'acv',
  analyticsFunnelProgress: 'afp',
  analyticsHourly: 'ahr',
  analyticsDaily: 'adl',
  analyticsMonthly: 'amn',
  analyticsConsent: 'acs',
  analyticsSalt: 'asl',
  analyticsVisitorUtm: 'avu',

  // Reporting & Export
  report: 'rpt',
  reportVisibilitySetting: 'rvs',
  reportSchedule: 'rsc',
  // RPT-CANON-001 (Phase 04) — canonical custom-report platform
  // (@puffin-core/report-platform): saved report definitions + per-run audit.
  reportDefinition: 'rdf',
  savedReportRun: 'srr',
  // RPT-CANON-001 (Phase 05) — scheduled-report delivery: one durable
  // per-attempt delivery-run record (running → delivered | failed).
  reportDeliveryRun: 'rdr',
  export: 'exp',

  // Error & Logging
  errorRecord: 'err',
  voiceAuditLog: 'val',
  voiceTemplate: 'vtl',

  // Jobs & Queues
  job: 'job',

  // Webhook
  webhookevent: 'whe',
  webhookSubscription: 'whs',

  // User Preferences & Setup
  preference: 'prf',
  progress: 'prg',
  signupsession: 'sgs',

  // Location
  locationHour: 'lhr',
  locationTypeDef: 'ltd',
  hierarchyLabel: 'lbl',

  // Relations & Links
  productsupplier: 'psl',
  transferitem: 'tfi',
  // 🗑️ `target: 'tgt'` was REMOVED by EPI-TYPE-RESTORATION-001 (2026-09-17). It was never an
  // entity — it existed solely so `IdMappingService.storeAndGetUuid()` could mint ONE prefix for
  // EVERY imported entity type, which is the exact conflation distinguishable prefixes exist to
  // prevent. 12,641,519 ids were born `tgt_`, and twelve live validators across four apps refused
  // them. The importer now mints `generateId(epiTypeFor(mapping.entityType))`.
  // 🔴 Do NOT recycle `tgt` for a new entity: a `tgt_` id in the wild is now unambiguously a
  // pre-remediation artifact, and the conformance contract keys on exactly that.

  // Migration & Import
  migrationImport: 'mgi',
  migrationProject: 'mgp',
  importIdMapping: 'iim',
  matchCandidate: 'mmc',
  matchGroup: 'mmg',
  mergeOperation: 'mmo',
  productLocationPrice: 'plp',
  pmMigrationApiKey: 'pmk',
  pmMigrationAuthAttempt: 'pma',
  pmMigrationBatch: 'pmb',
  pmMigrationSnapshot: 'pms',
  pmMigrationTableError: 'pte',
  pmMigrationRecord: 'pmr', // pm_migration_records — per-record audit trail (T100-C C1b)
  pmStagingRecord: 'psr',
  importLog: 'pml',
  pmbPlan: 'ppn',
  dataQualityReport: 'dqr',
  reconciliationReport: 'rcr',
  verificationSignOff: 'vso',
  deltaSyncRun: 'dsr',
  configAssessment: 'mca',
  addressValidation: 'mav',
  addressBatchRun: 'mab',
  piiAccessLog: 'mpl',
  piiClassification: 'mpc',
  migrationRecipe: 'mrc',
  decommissionChecklist: 'mdc',
  migrationCommunication: 'mgc',
  departmentMapping: 'mdm',
  entityReview: 'mer',
  duplicateGroup: 'mdg',

  // Change Logs
  changeLog: 'clg',

  // Security & Store Operations
  securityPolicy: 'spo',
  storeOpeningSession: 'sos',
  storeOperationSetting: 'sop',
  locationOperatingSetting: 'los',
  stationSession: 'sss',
  stationUnlockHistory: 'suh',
  // POS-SESSION-STATION-TIMERS-001 OFF-U10: a stretch a register spent offline, reported on reconnect
  // (`stationOfflineWindows`). `sow` measured collision-free against the full registry.
  stationOfflineWindow: 'sow',
  // POS-SESSION-STATION-TIMERS-001 OFF-U12 (B): what a register kept offline while it held no station, reported on
  // reconnect (`stationlessJournalReports`). `sjr` measured collision-free against the full registry.
  stationlessJournalReport: 'sjr',
  // G1 / in-store-experience Phase 0c (2026-06-17): label-reprint receipt — the
  // counter/kiosk "reprint a shipping label" audit record. Mirrors the
  // drop_off_receipts shape. `lrp` verified collision-free against the full
  // registry; the `should have no duplicate prefixes` guard catches regressions.
  labelReprintReceipt: 'lrp',
  sessionTimeoutPolicy: 'stp',
  locationTypePolicy: 'ltp',
  operationAuthLevel: 'opl',

  // QuickBooks (additional)
  quickBooksAuditLog: 'qal',
  quickBooksSyncLog: 'qsl',

  // Accounting — the vendor-neutral domain the POS daily sync writes over the ONE connector factory
  // (SECURITY-AND-CONNECTORS-001 P2.1). They replace the QuickBooks-named mapping and sync-log entities,
  // whose rows M4 moves and remints; `aam` / `asy` verified collision-free against the full registry.
  accountingAccountMapping: 'aam',
  accountingSyncLog: 'asy',
  // P2.3b: the store → ledger dimension per connection (`accounting_store_mappings`); `ast` issued by THINKPAD
  // 2026-10-21 (coord log/stamps.md), measured free on main, the push-16 assembly and every THINKPAD lane.
  accountingStoreMapping: 'ast',
  // P2.7: the ONE adjusting entry a late void posts against a posted day, per transition
  // (`accounting_posting_adjustments`); `apa` measured free on D2 part 2's tree and the DYLAN-PC census store.
  accountingPostingAdjustment: 'apa',

  // Voice (additional)
  voicePlatformConfig: 'vpc',
  voiceTenantConfig: 'vtc',

  // Public-facing
  signup: 'sgu',
  request: 'req',

  // RDR-001: Reference Data Remediation (DB-driven reference tables)
  deviceType: 'dvt',
  locationType: 'lty',
  locationStatus: 'lst',
  // `net` — its tenant table `notificationEventTypes` was DROPPED by pos `20260928140000` (LEAVE-CANON-001;
  // pos Batch 549, 2026-10-05); the catalog is served from `NOTIFICATION_EVENT_TYPE_ROWS` constants and no
  // live code mints this type. It stays REGISTERED for one reason, measured 2026-10-05: two APPLIED RDR-001
  // migrations mint it (`20260223000002`, `20260225000004`), and `@puffin-core/docs-generator` EXECUTES
  // applied migrations to document their schema — with the registration gone, SCHEMA.md generation refused
  // for each of the six apps tried ("Unknown entity type"). Delete it only with an owner-approved edit of those two
  // applied files (FULL-MERGE-1001 fix-post-phase-t, owner question).
  notificationEventType: 'net',
  alertConfiguration: 'alc',
  // STORE-ALERTS-001 (2026-10-09) — `cmp_*.storeAlertFirings` row: one store alert raised (claimed before its
  // dispatch, deduplicated by key, finalised after). `saf` is distinct from `sar` (signupActivationRun) and `baf`
  // (backupAlertFiring).
  storeAlertFiring: 'saf',
  // BDR-CONSOLE-001 (2026-06-26) — `public.backupAlertRules` row: a platform
  // operator's backup alert rule (backup_failure / backup_age / storage_size).
  // `bar` is distinct from `alc` (alertConfiguration), `alt` (alert), `ald`
  // (alertDestination), `abu` (abuseAlert), `aal` (anomalyAlert).
  backupAlertRule: 'bar',
  // BDR-CONSOLE-001 hardening (2026-06-27) — `public.backupAlertFirings` row: an
  // immutable record of one alert rule firing + its per-channel dispatch outcome.
  // `baf` is distinct from `bar` (backupAlertRule).
  backupAlertFiring: 'baf',
  // SCALE-JOB-001 W8-F (2026-08-08) — `public.backupSchedules` row: a persisted
  // backup schedule (cron + options + durable execution stats + the claimable
  // `nextExecution` firing token). `bsc` is distinct from `bar`/`baf` above and
  // every existing `b*` prefix.
  backupSchedule: 'bsc',
  // HARD-RESTORE-001 (2026-08-30) — `public.recoveryJobs` row: one restore-engine run
  // (S3-artifact or PITR-fork) with real phase/progress/verification state. `rcj` is
  // distinct from `rcp` (recipient-class prefixes), `rec*` shapes, and every existing
  // `r*` prefix.
  recoveryJob: 'rcj',
  // HARD-RESTORE-001 (2026-08-30) — `public.restoreTargets` row: one engine-provisioned
  // SCRATCH cluster (isolation-first ruling: never a database on the live cluster),
  // registered for the 72h audited auto-drop. `rst` is distinct from `rsh` (rushOption)
  // and `rcj` above. Credentials are deliberately NEVER persisted on this row.
  restoreTarget: 'rst',
  // TENANT-CREATION-JOB-001 (2026-10-07; owner rulings Q-TCJ-3 / Q-TCJ-4: long work is always a job) — one run of a
  // long platform operation the console follows (`@puffin-core/operation-runs`), one prefix per run table:
  // `public."tenantCreationRuns"` (`tnc`; `tcr` is trainingCourse), `public."backupRuns"` (`bkr`, distinct from `bar` /
  // `baf` / `bsc` above), `public."blogSyncRuns"` (`bsr`) and `public."signupActivationRuns"` (`sar`).
  tenantCreationRun: 'tnc',
  backupRun: 'bkr',
  blogSyncRun: 'bsr',
  signupActivationRun: 'sar',
  // VALIDATION-ECHO-001 P3i (owner ruling R-11) — `public."signupReminders"`: the ONE signup reminder an address ever
  // receives, its claim and its opt-out. `rmd` ("reminder") was measured free on every lane tree, every ref and the
  // push-16 assembly `41c23c9ece` (coordinator verification 2026-10-21) before it was chosen.
  signupReminder: 'rmd',
  // VALIDATION-ECHO-001 P3j (owner ruling R-10, "Accept BUILD") — `public.signup_sessions."applicationId"`: a signup
  // application's PUBLIC, non-secret identity, which the platform console's applicant directory names, links and
  // audits. The session id is a seven-day bearer credential and never leaves the server. `apl` was measured free on
  // every ref tip (631) and every lane tree before it was chosen (2026-10-21); `sga` / `sgr` / `sap` were taken.
  signupApplication: 'apl',
  laborRule: 'lbr',
  auditRetentionPolicy: 'arp',
  systemLocale: 'slc',
  // `svc` — its tenant table `serviceConfigurations` was DROPPED by pos `20260927160000` (LEAVE-CANON-001 R14;
  // pos Batch 548, 2026-10-05); no model or route read it and no live code mints this type. It stays
  // REGISTERED only because two APPLIED migrations mint it (`20260223000004`, `20260226000001`) and the docs
  // generator executes applied migrations — see `notificationEventType` above.
  serviceConfiguration: 'svc',
  pricingTier: 'prt',
  volumeDiscountRule: 'vdr',
  rushOption: 'rsh',

  // Testing (for test helpers only)
  test: 'tst',
  testgoal: 'tgl',
  testtask: 'ttk',

  // ── E-Commerce (PurchasePuffin) ──
  shoppingCart: 'sct',
  cartItem: 'sci',
  productVariant: 'pvr',
  contentPage: 'cpg',
  contentVersion: 'cvn',
  // LIFE-SHORTLISTED-001 P6.3 (2026-09-25) — contentBlock: one block inside a CMS page's `blocks`
  // list (jsonb on `pages` / `pages.draft`), minted by `@puffin-core/block-editor` when an author adds
  // it, so each row keeps one identity across edits and reorders.
  contentBlock: 'cbk',
  // LIFE-SHORTLISTED-001 P7.2 (2026-09-25) — siteMedia: one image a tenant uploaded for its website
  // (`cmp_*.site_media`), processed once into JPEG|PNG + WebP + AVIF and served same-origin at
  // `/media/<mediaId>.<ext>`.
  siteMedia: 'sme',
  // LIFE-SHORTLISTED-001 P10.2 (2026-09-27) — siteLink: one owner-editable destination a tenant's
  // site links to (`cmp_*.purchase_site_links`): a social account, the newsletter, a shop. Rendered
  // in the site footer and by the siteLinks block; an inactive link is never rendered.
  siteLink: 'slk',
  // PROTECTOR-SMS-001 Unit 1 (2026-10-26, STORE-SITES-CONVERGENCE-001 W1-1's core, R-SSC-12) — tenantPageDocument:
  // one published CMS page rendered to a complete HTML document at publish, per locale (`cmp_*."tenantPageDocuments"`),
  // served to a visitor with no script. Measured free on all three boxes.
  tenantPageDocument: 'tpd',
  // LIFE-SHORTLISTED-001 P11 (2026-09-28) — content collections: a tenant's editorial collection
  // (`cmp_*.purchase_content_collections`, e.g. "edits"), its controlled category vocabulary
  // (`purchase_content_categories`), one editorial item (`purchase_content_items`) and an affiliate
  // program whose required statement travels with every item that names it
  // (`purchase_affiliate_programs`).
  contentCollection: 'cco',
  contentCategory: 'ccg',
  contentItem: 'cci',
  affiliateProgram: 'afg',
  // 🗑️ `webhook: 'whk'` and `webhookLog: 'whl'` were REMOVED with purchase-api's partner webhook
  // registry (SECURITY-AND-CONNECTORS-001 SEC-A3-QUOTES, owner-ruled wire-or-prune). Their only
  // minter was that registry's service, and its `webhooks` / `webhook_logs` tables never existed on
  // any tenant schema, so no `whk_` or `whl_` id was ever written. Outbound webhooks are
  // `@puffin-core/webhooks`, whose ids are `webhookSubscription`.
  partner: 'ptn',
  navigationMenu: 'nmn',
  navigationItem: 'nvi',
  designProof: 'dpf',
  customizationUpload: 'cup',
  seoMetadata: 'smd',
  pricingRule: 'prl',
  customerAddress: 'cad',
  customerPhone: 'cph',
  // NUDGE-LINK-001 P3-B (owner ruling R-5, 2026-09-06) — a tenant's request to attach a vanity
  // domain (`<tenant>.purchase_domain_requests`): the write path `companies.customDomain` never had.
  domainRequest: 'dmr',
  // NUDGE-LINK-001 P3-B — Cart Recovery: the per-tenant settings row and the per-(cart, touch)
  // send log (`<tenant>.purchase_cart_recovery_{settings,sends}`).
  cartRecoverySettings: 'crg',
  cartRecoverySend: 'cre',
  // MUTATION-INSTRUMENT-001 CART1b — a storefront customer's marketing-email consent (one row per grant, closed by
  // its withdrawal) and the one-click unsubscribe link each marketing email carries
  // (`<tenant>.purchase_marketing_{consents,unsubscribe_links}`).
  marketingConsent: 'mcn',
  marketingUnsubscribeLink: 'mul',
  // NUDGE-LINK-001 P3-B — Fulfillment/Dropship: the per-(tenant, supplier) drop-ship
  // configuration (`<tenant>.purchase_dropship_suppliers`). Order shipments
  // (`purchase_order_shipments`) reuse the existing `shipment` prefix — same concept, one prefix.
  dropshipSupplier: 'dsp',
  productPrice: 'ppr',
  // `taxJurisdiction` (`txj`) and `taxRule` (`trl`) were pruned by MUTATION-INSTRUMENT-001 B238
  // (owner ruling Q-TAX1-1: tax is the store location's rate unless the item or the customer is
  // exempt — no jurisdiction or category rule tables); their tables held 0 rows in every schema.

  // ── Omnichannel sales channels (OMNI-001: single item-master, many channels) ──
  salesChannel: 'chn',
  productChannelListing: 'chl',
  channelFulfillmentLocation: 'cfl',

  // ── PatrolPuffin (Carrier Invoice Audit) ──
  patrolCarrierAccount: 'pca',
  patrolInvoice: 'pin',
  patrolInvoiceLine: 'pil',
  patrolQuote: 'pqt',
  patrolDiscrepancy: 'pds',
  patrolClaim: 'pcl',
  patrolClaimLine: 'pcn',
  // PERMISSIONS-CANON-001 row 1d (U1): a credit a later carrier invoice carries against a missed delivery
  // guarantee (`patrol_claim_credits`, patrol `20261010199220`).
  patrolClaimCredit: 'pcd',
  patrolAuditRule: 'par',
  patrolSubscription: 'psb',
  // `pdr` (patrolDemoRequest) was retired with `public.patrol_demo_requests` (OWNER-RULINGS-WALK-BUILDS N167,
  // `20261006100960`): patrol's demo form persists into `public."platformLeads"`, which mints `contactSubmission`
  // ids, and the fold re-mints each old row's id from it; nothing mints `pdr` any more.

  // SHIPMENT-PACKAGES-002 U3 (2026-10-05) — `public.patrol_das_zip_lists`: one carrier's published
  // delivery-area-surcharge ZIP list, effective from a date (carrier reference data, not tenant
  // data; DR-010's evidence). `dzl` measured free of the registry, distinct from every `d*` prefix.
  patrolDasZipList: 'dzl',

  // PATROL-QUOTE-VS-INVOICE-001 U7 (2026-11-02) — `cmp_*.patrol_package_audits`: one row per invoice line (one
  // package), the named outcome the quote-vs-invoice audit reached for it; `cmp_*.patrol_package_audit_components`:
  // one row per component of that audit. `ppa` / `ppc` measured free of the registry, distinct from every `pp*`
  // prefix (`ppl`, `ppp`, `ppn`, `ppr`, `pps`, `ppg`).
  patrolPackageAudit: 'ppa',
  patrolPackageAuditComponent: 'ppc',

  // ── PatrolPuffin Crawler ──
  crawlerApiKey: 'cak',
  crawlerSyncState: 'css',

  // ── PayDayPuffin ──
  payrollMapping: 'pym',
  payrollRun: 'pyr',
  payrollRunItem: 'pri',
  paydaySyncLog: 'pdy',
  paydaySettings: 'pys',
  paySchedule: 'psh',
  payApprovalChain: 'pac',
  payApprovalRecord: 'pap',
  payDeductionItem: 'pdi',
  // PAYDAY-RUN-LIFECYCLE-001 (B289 F-QB1-15) — `payday_payroll_tax_items`: one tax line per tax, payer and employee of
  // a processed run. `pti` RESERVED for it by the coordinator (THINKPAD, 2026-10-19T05:37Z).
  payTaxItem: 'pti',
  glExport: 'glx',
  payrollAdjustment: 'pya',
  anomalyAlert: 'aal',
  bankAccount: 'bac',
  // `bnk` (bankBalance) was retired with `payday_bank_balances` (PAYDAY-RUN-LIFECYCLE-001, payday `20261010179500`): a
  // balance is never stored, the funding check reads the live balance, and nothing mints `bnk` any more.
  // `txf` (taxFiling) was retired with `payday_tax_filings` (PAYDAY-UAT-REMEDIATION-001 U8 W8-6, payday
  // `20261010200190`): the payroll processor files and pays the company's tax and PayDay reads its payments, the table
  // held 0 rows in every schema, and nothing mints `txf` any more.
  contractorMapping: 'cnt',
  contractorPayment: 'cpy',
  contactSubmission: 'ctc',
  // NUDGE-LINK-001 P3-A (owner ruling R-5, 2026-09-06) — the admin surfaces published help articles walked while
  // nothing served them: the PTO accrual policy and its per-tier rates, court-ordered garnishments, and the GL account
  // map `POST /gl-exports` has always REQUIRED in its body with nothing to persist it.
  // `txp` (paydayTaxProfile) and `txa` (paydayTaxJurisdiction) were retired with `payday_tax_profiles` /
  // `payday_tax_jurisdictions` (PAYDAY-UAT-REMEDIATION-001 Unit 8, payday `20261010200050`): the payroll processor holds
  // the company's tax setup, both tables held 0 rows in every schema, and nothing mints either prefix any more.
  // LEAVE-CANON-001 Phase 8a: the PTO policy became one entitlement policy PER LEAVE TYPE (owner
  // ruling R-49, "Time Off Policies"). Renamed with its tables, no alias — no row ever carried the
  // old `pto` / `ptr` prefixes (0 rows in every schema, measured 2026-09-24).
  paydayLeavePolicy: 'lvp',
  paydayLeaveAccrualTier: 'lvt',
  paydayGarnishment: 'grn',
  // PAYDAY-UAT-REMEDIATION-001 U9 (WI-6): a remittance line per run and order the employer remits (M2 20261010199290).
  paydayGarnishmentRemittance: 'grm',
  // PAYDAY-UAT-REMEDIATION-001 U9 (WI-9): a court order attached to a garnishment (M3 20261010199295).
  paydayGarnishmentDocument: 'gdc',
  paydayGlMapping: 'glm',
  // PAYDAY-UAT-REMEDIATION-001 Unit 5 (owner, 2026-10-28: remote people in MD and NH): where a team member WORKS —
  // `payday_work_locations` (a store, carrying only its `locationId`, or a remote address) and
  // `payday_employee_work_assignments` (who works where, from a date).
  workLocation: 'pwl',
  employeeWorkAssignment: 'pwa',
  // Unit 6: a person on a pay schedule from a date (`payday_pay_schedule_assignments`), and a store's, or with no store
  // the tenant's, DATED default schedule (`payday_pay_schedule_defaults`).
  payScheduleAssignment: 'psg',
  payScheduleDefault: 'pss',
  // Unit 7: a person on a leave policy from a date (`payday_leave_policy_assignments`), and the tenant's or a work
  // state's DATED default policy (`payday_leave_policy_defaults`).
  leavePolicyAssignment: 'lpa',
  leavePolicyDefault: 'lpd',
  // PAYDAY-RUN-LIFECYCLE-001 A20b-2: a final paycheck's confirmed leave payout per leave type
  // (`payday_separation_payouts`). `sep` measured free on every THINKPAD tree and all 35 HAMPTONS
  // trees (THINKPAD, 2026-10-29).
  paydaySeparationPayout: 'sep',
  // Unit 8 W8-7: a payroll processor's notice, stored on PayDay's side (`payday_provider_notices`). `pnt` is
  // `prepNotification`'s.
  paydayProviderNotice: 'pvn',

  // ── PalmPuffin (Associate Self-Service) ──
  availabilityPattern: 'avp',
  availabilityException: 'ave',
  timeOffRequest: 'tor',
  timeOffBalance: 'tob',
  // LEAVE-CANON-001 8c (owner ruling R-50): the accrual ledger — one marker per payroll run palm has
  // accrued, one entry per (run, employee, leave type) it credited.
  leaveAccrualRun: 'lar',
  leaveAccrualEntry: 'lae',
  // PAYDAY-UAT-REMEDIATION-001 Unit 7: one balance statement a jurisdiction requires, per person, leave type and period
  // (`palm_leave_balance_statements`; Oregon's quarterly statement, for example).
  leaveBalanceStatement: 'lbs',
  // LEAVE-CANON-001 8c-2 (R-46 / R-21): one record per closed leave year, per employee and type — what
  // was carried, what was forfeited, and under which rule.
  leaveRollover: 'lro',
  // LEAVE-CANON-001 R14 (D-R14-2, owner ruling R-68): an employee's ask to withdraw APPROVED leave —
  // a request with its own lifecycle (`palm_time_off_withdrawals`), no longer a mark on the leave.
  timeOffWithdrawal: 'tow',
  // PALM-OFFLINE-PUNCH-SYNC-001 (owner rulings Q-POS-2 / Q-POS-3): a punch an employee's device could not record,
  // reported to palm (`palm_offline_punch_reports`) and listed for the store's managers in pos-web's Timecards approval.
  offlinePunchReport: 'opr',
  shiftMarketplacePosting: 'smp',
  hoursPreference: 'hrp',
  palmNotificationPreference: 'ntp',
  teamMessage: 'tmg',
  palmSetting: 'pst',
  pushSubscription: 'pps',
  recognitionCategory: 'rct',
  userProfile: 'pup',
  taskTemplate: 'tkt',
  taskInstance: 'tki',
  taskItemCompletion: 'tkc',
  palmDocument: 'pdc',
  sentimentEntry: 'sen',
  palmSurvey: 'psv',
  palmSurveyResponse: 'svr',
  documentAcknowledgment: 'dak',
  // UAT-BLOCKERS-2026-09-18: calendarSubscription = one employee's subscription to their own
  // schedule from a calendar app — `palm_calendar_subscriptions` (the issuance its link is valid for).
  calendarSubscription: 'csb',

  // ── PalmPuffin Compliance ──
  breakViolation: 'bkv',
  // breakPreApproval ('bpa') = manager pre-approval record for a
  // scheduled break window (Phase 3c). The template rule source filters
  // out template-adherence violations that have a matching pre-approval,
  // emitting a `meal_break_pre_approved` / `rest_break_pre_approved`
  // informational record instead so the audit trail stays complete.
  breakPreApproval: 'bpa',
  // posBreakViolation ('pbv') = Phase 3d POS-local break violation
  // record, written by pos-api's TimecardService.clockOut() when the
  // shared `@puffin-core/break-compliance` evaluator produces
  // violations for a POS clock-out. Separate from `breakViolation`
  // ('bkv') so each app owns its own violations table and there is no
  // cross-schema write on the hot path. The ecosystem byte-equality
  // gate proves both tables receive identical semantics given
  // identical inputs.
  posBreakViolation: 'pbv',
  // breakVerdictRelay ('bvr') = OWNER-RULINGS-WALK-BUILDS L4 F6 (owner ruling Q-L4-3, 2026-10-05):
  // one pos-api `pos_break_verdict_relays` row per superseded shift a pos re-judgement owes palm —
  // written in the re-judgement's transaction, acknowledged when palm takes the supersede, and
  // re-sent by the reconciliation sweep until it does.
  breakVerdictRelay: 'bvr',
  // emailOutbox ('eob') = ECOSYSTEM-EMAIL-001 lane C (commerce transactional email): one purchase-api
  // `purchase_email_outbox` row per email a store owes a person (an order confirmation, a shipping notice,
  // a quote acknowledgement, a staff copy) — written in the transaction that moved the order, shipment or
  // quote, and sent by the dispatcher until it is delivered or has failed for good.
  emailOutbox: 'eob',
  timecardAttestation: 'tat',
  emergencyBroadcast: 'ebc',
  broadcastAcknowledgment: 'bak',
  certification: 'cer',
  scheduleComplianceLog: 'scl',

  // ── PalmPuffin Gamification & Training ──
  point: 'pts',
  streak: 'srk',
  achievementBadge: 'abg',
  trainingCourse: 'tcr',
  trainingAssignment: 'tas',

  // ── PalmPuffin Authenticator ──
  authenticatorDevice: 'adv',
  // AUTHENTICATOR-CHALLENGE-FANOUT (2026-08-29). Both ids were hand-rolled
  // strings before this initiative — `chg_${Date.now()}_${idCounter}` and
  // `enr_${Date.now()}_${idCounter}`, each off a MODULE-LEVEL mutable counter
  // living inside a factory, so two service instances in one process mint
  // colliding ids on a security-critical identifier. EPI-001 violation on both.
  //
  // `ach` and `aen` are the prefixes the design docs already specify
  // (`GUIDES/PalmPuffin/authenticator/{ARCHITECTURE,CHALLENGE_FLOW}.md` and
  // `ENROLLMENT.md`), so the guide wins that drift rather than the code.
  // Both collision-checked against the full registry: distinct from `adv`
  // (authenticatorDevice), `act` (aiConversationTurn), `aic`, `agt`, `aum`,
  // `asi`, `asr`, `apt`, `abg`, `att` (attempt) and every other `aXX` entry —
  // the `should have no duplicate prefixes` invariant is the standing guard.
  authenticatorChallenge: 'ach', // Redis-stored push-2FA challenge (60 s TTL)
  authenticatorEnrollment: 'aen', // Redis-stored enrollment handshake (300 s TTL)

  // ── PlannerPuffin (Workforce Optimization) ──
  holiday: 'hol',
  department: 'dep',
  // PLN-DEPT-PLAN-001 (2026-09-08) — the department PLANNING axis, distinct on
  // sight from `dep` (the POS reporting leaf, PCAT-AXIS-001, which planner
  // never edits). A `departmentGroup` is a planner-owned NAMED SET of POS
  // departments (`planner_department_groups`): company-scoped by default,
  // plan-scoped when a plan overrides the grouping, plus the one system
  // "Unassigned" group that carries dollars no attribution rule could place.
  // `departmentGroupMember` is the join row (`planner_department_group_members`)
  // holding `posDepartmentId` — the membership identity is the POS row id
  // (D-9), not a name, because after the PostalMate canon fold there is exactly
  // one `departments` row per name tenant-wide and a name key would break on
  // rename. Two entities, two prefixes: a group id and a membership id must
  // never be confusable in a log line.
  departmentGroup: 'dpg',
  departmentGroupMember: 'dgm',
  // PLN-DEPT-GRAIN-001 L4 (2026-09-23) — planner-api
  // `planner_department_planning_profiles`: one row per POS department planner
  // has an OPINION about — its Daily / Monthly planning grain (owner ruling
  // G-5), whether an admin set it or the nightly suggestion did, and the latest
  // suggestion pos-api's demand profile (`pdp`, below) produced. An absent row
  // MEANS Daily-by-suggestion. Distinct from `dpg` (the group the department
  // sits in) and `pdp` (the pos-api profile it is suggested from).
  departmentPlanningProfile: 'dpp',
  // PLN-DEPT-PLAN-001 DP7-C-5 (2026-09-25) — planner-api
  // `planner_department_grouping_checkpoints`: one durable grouping + grain
  // SNAPSHOT (owner ruling Q-DP7-2a), auto-captured before the first grouping
  // write of an editing session and by every restore (so a restore is itself
  // reversible). `dgk` because `dgc` is the dashboardGroupConfig row and `dgm`
  // the membership row — a checkpoint id must not be confusable with either.
  departmentGroupingCheckpoint: 'dgk',
  salesPlan: 'sap',
  salesPlanItem: 'spi',
  // PLN-SP-STEP2-003 (2026-05-23) — per-period config row in
  // planner_sales_plan_periods carrying solveFor / plannedAos /
  // plannedTransactions / forecastMethod / forecastParams. Distinct
  // from salesPlanItem (per-day grain) and salesPlan (per-plan).
  salesPlanPeriod: 'spp',
  laborBudget: 'lbd',
  laborBudgetItem: 'lbi',
  // PLN-LB-UX-003 (2026-09-01) — laborBudgetDayModel: one row per budget ×
  // dayOfWeek in planner_labor_budget_day_models — the budget's per-day
  // assumption SNAPSHOT (open/close overrides, prep/cleanup minutes, headcount
  // per block, open-floor hours). Typed columns, not metadata JSONB, so an edit
  // re-entry reproduces the authoring state by construction.
  laborBudgetDayModel: 'lbm',
  scheduleDraft: 'scd',
  scheduleDraftEntry: 'sde',
  // PLN-SCHED-LIFECYCLE-001 (2026-07-07) — schedulePublication: one row per
  // publish ACT in the planner commitment ledger (planner_schedule_publications,
  // @puffin-core/commitment-ledger createPublicationModel).
  schedulePublication: 'spb',
  // PLN-SCHED-LIFECYCLE-001 P3 (2026-07-08) — the execution layer:
  // scheduleAbsence: a reported call-out/absence against a committed shift
  // (planner_schedule_absences). scheduleDayClosure: a location+date close-day
  // act (planner_schedule_day_closures). excludedDay: the canonical anomaly
  // registry row that keeps closed/anomalous days out of forecasts
  // (planner_excluded_days). scheduleDeltaEvent: one row per fanned-out
  // schedule delta (planner_schedule_delta_events). scheduleDeltaBatch: the id every event of ONE
  // fan-out shares (`batchId`; the palm relay's idempotency key) — a batch is not one of its events
  // (REPAIR-LEDGER-AND-TYPED-ID-001 T3 S5, 2026-09-26).
  scheduleAbsence: 'sab',
  scheduleDayClosure: 'sdc',
  excludedDay: 'exd',
  scheduleDeltaEvent: 'sdl',
  scheduleDeltaBatch: 'sdb',
  // LEAVE-CANON-001 P3a-2 (2026-09-19) — leaveBlackout: a named span of civil dates, at one store
  // or chain-wide, during which planned absence is refused or flagged (planner_leave_blackouts).
  leaveBlackout: 'lbo',
  // LEAVE-CANON-001 P5-4 (2026-09-23) — approvalDelegation: an approver's authority handed to a
  // delegate over a span of civil dates, optionally at one store (planner_approval_delegations).
  approvalDelegation: 'apd',
  // LEAVE-CANON-001 Phase 3 (2026-09-24) — leaveShiftConflict: a published shift an approved leave
  // request collides with (`cmp_*.planner_leave_shift_conflicts`, D-P6-3).
  leaveShiftConflict: 'lsc',
  // LEAVE-CANON-001 Phase 4 (2026-09-24) — priorPeriodLeaveCorrection: approved leave hours a
  // LOCKED payroll run did not pay, reconciled at the next run
  // (`cmp_*.payday_prior_period_leave_corrections`, R-36).
  priorPeriodLeaveCorrection: 'plc',
  shiftTemplate: 'sft',
  shiftTemplateSlot: 'sts',
  // BULK-IMPORT-001 (2026-05-25) — descriptor-driven bulk-import engine.
  // bulkImport: a bulk-import job (bulk_imports). importRecord: a tombstone-ledger
  // row tracking one committed record for exact rollback (bulk_import_records).
  // taxWithholding: a PayDay W-4 withholding row (payday_employeeTaxWithholdings).
  bulkImport: 'bim',
  importRecord: 'bir',
  // BULK-IMPORT-002 (2026-05-29) — bulkImportMapping: a saved column-mapping
  // (import profile) reused across repeat imports of a topic (bulk_import_mappings).
  bulkImportMapping: 'bmp',
  taxWithholding: 'twh',
  splhTarget: 'spt',
  coverageRequirement: 'cvr',
  // STN-001 (2026-06-03): `functions → stations` Gen-0 rename. The legacy
  // planner scheduling entity types (the prior `fnc` / `fql` prefixes) are
  // replaced — nothing mints `fnc_`/`fql_` IDs anymore. Existing rows keep
  // their minted IDs (opaque strings, EPI-001 — never rewritten). The
  // POS in-store `stationSession:'sss'` / `stationUnlockHistory:'suh'` prefixes
  // are an unrelated concept and stay untouched.
  schedulingStation: 'stn',
  stationQualification: 'stq',
  promotionalEvent: 'pev',
  plannerSetting: 'pfs',
  dowPattern: 'dwp',
  planTemplate: 'plt',
  fiscalPeriod: 'fpr',
  // PLN-CALENDAR-ERA-001 (2026-07-12): effective-dated calendar governance.
  // calendarEra = a row in the per-tenant `calendar_eras` dimension (the
  // calendar config in force over a date span; one era per day, EXCLUDE-
  // enforced). fiscalYearClose = an append-only row in `calendar_year_closes`
  // (the intentional, audited freeze of a fiscal year — never reopened).
  // Both minted via @puffin-core/fiscal-calendar/persistence model factories.
  calendarEra: 'era',
  fiscalYearClose: 'fyc',
  // PLN-CALENDAR-ERA-001 E6 (2026-07-12): calendarRecast = one executed
  // calendar re-cast act (the N-old→M-new container re-partition through
  // CalendarRecastService.executeRecast). The id is provenance-only — stamped
  // into plan/budget `metadata.recast` + the audit chain; no table carries it
  // as a PK. 'rcs' because the natural 'rct' is owned by recognitionCategory.
  // PLN-CALENDAR-ERA-002 Q#10 closure (2026-07-13): 'rcs' is PERMANENT — a
  // rename adds nothing (the prefix never appears in a PK or FK), and live
  // recast provenance can exist at any time, so the safe-rename window is
  // gone the moment a tenant executes a supersession. Do not rename.
  calendarRecast: 'rcs',
  planScenario: 'psc',
  // employeeSchedule ('esc') = the published, employee-visible schedule row
  // in the tenant-schema employee_schedules table. Owned by planner-api
  // ScheduleDraftService.publish() and read cross-schema by palm-api
  // ScheduleLookupService.findForPunch(). Distinct from 'schedule' ('sch',
  // legacy pos-api schedule_entries) and 'scheduleDraft' ('scd', pre-publish
  // planner draft). Phase 3a (break-compliance integration).
  employeeSchedule: 'esc',

  // ── PrepPuffin (Enterprise Training & LMS) ──
  program: 'pgm',
  course: 'crs',
  courseModule: 'mod',
  lesson: 'lsn',
  lessonContent: 'lcn',
  assessment: 'asm',
  assessmentQuestion: 'qst',
  questionPool: 'qpl',
  learningPath: 'pth',
  learningPathItem: 'lpi',
  prepEnrollment: 'enr',
  prepProgress: 'ppg',
  assessmentAttempt: 'aat',
  assessmentResponse: 'ars',
  observationChecklist: 'obs',
  observationRecord: 'obr',
  certificationDefined: 'cdf',
  certificationAward: 'caw',
  assignmentRule: 'arl',
  contentMarketplaceListing: 'mkl',
  contentPurchase: 'cpu',
  prepSetting: 'pse',
  prepAuditLog: 'pag',
  prepNotification: 'pnt',
  prepNotificationPreference: 'pnp',
  // MUTATION-INSTRUMENT-001 B162/B177 (2026-09-27) — one stored content asset delivered through the signed
  // content route: a `prep_content_assets` lesson upload (a video source, a document, a caption track, a SCORM
  // package) and, since RUG-ACADEMY-001, a file of a PurchasePuffin training course. Its holder names it by this
  // opaque id, never by a storage URL. Named `prepContentAsset` until PURCHASE-SAFETY-001 (GAMING ruling RUG-1):
  // the key names the content primitive, not one app; the prefix never moved, so no stored id changed.
  contentAsset: 'cas',
  // FULL-MERGE-1001 round 2 — `prep_content_upload_sessions`: one RESUMABLE, CHUNKED upload in flight. A
  // lesson file larger than the edge's request-body cap (Cloudflare Free: 100 MB) arrives in chunks under
  // it, each idempotent by index; the completed session becomes one `contentAsset`.
  prepContentUpload: 'pcu',
  generationJob: 'gen',
  llmProvenance: 'llp',
  // ── Spaced-retrieval nudges (PREP-SHORTS-DELIVERY-001 Phase 10) ──
  // spacedRetrievalNudge ('nud') — row in `<tenant>.prep_spaced_retrieval_nudges`,
  // the authored 24h/72h/1w reinforcement prompt for one training short.
  // nudgeDelivery ('ndl') — row in `<tenant>.prep_nudge_deliveries`, one
  // learner's scheduled instance of that nudge. UNIQUE(enrollmentId, nudgeId)
  // is what makes the dispatch sweep idempotent.
  spacedRetrievalNudge: 'nud',
  nudgeDelivery: 'ndl',

  // ── Break-compliance rule storage (Phase 4+) ──
  // jurisdictionBreakRule ('jbr') — row in public.jurisdictionBreakRules
  // carrying the effective-dated statutory break rule for a US jurisdiction
  // (state, DC, or city overlay). The DatabaseRuleResolver in
  // @puffin-core/break-compliance reads from this table with a 5-minute
  // cache and falls back to the in-process seed if the DB is unreachable.
  jurisdictionBreakRule: 'jbr',
  // jurisdictionRuleChange ('jrc') — platform-admin triage row written when
  // a rule is inserted / updated / tombstoned. Phase 5 fan-out service
  // reads this table when notifying affected tenants.
  jurisdictionRuleChange: 'jrc',
  // tenantRuleChangeNotification ('trc') — per-tenant in-app notification
  // row written by the Phase 5 fan-out service when a rule change affects
  // stores under the tenant's locations.
  tenantRuleChangeNotification: 'trc',
  // complianceOnboardingTask ('cot') — platform-admin triage row written by
  // ComplianceOnboardingService when a newly onboarded tenant (or new store)
  // enters a jurisdiction whose rule is unverified or stale. Surfaced in the
  // console at /compliance/onboarding.
  complianceOnboardingTask: 'cot',

  // ── PortPuffin (Business Phone / Voice / AI Receptionist) ──
  phoneIdentity: 'pid',
  // phoneIdentityLocationBinding ('pib') — Phase 6 many-to-many join row
  // linking an F-MB phoneIdentity (brand) to a tenant location. A brand can
  // bind to 1+ locations with at-most-one marked primary (enforced by DB
  // partial unique index on (phoneIdentityId) WHERE isPrimary=TRUE AND
  // deletedAt IS NULL). Distinct from phoneIdentity ('pid') — the binding
  // is a separate lifecycle (soft-deleted independently, can be re-bound).
  phoneIdentityLocationBinding: 'pib',
  phoneNumber: 'pnu',
  call: 'cll',
  callEvent: 'cev',
  callRecording: 'crc',
  voicemail: 'vml',
  // Phase 10 (Voicemail Capture + Transcription):
  // voicemailConfig ('vrc', voicemail retention config) — per-tenant cascade
  // table keyed on (companyId, phoneIdentityId) holding retention days,
  // legal-hold default, delete strategy (soft|hard), fallback greeting
  // text/audio URL, and maxDurationSec override. NULL phoneIdentityId is
  // the tenant-default row. Distinct from voicemail ('vml') which is the
  // recorded-audio row; the two have independent lifecycles.
  voicemailConfig: 'vrc',
  // Phase 11 (Call Recording + State-Aware Consent Announcement):
  // callRecordingConsentMatrix ('crm') — public-schema table holding the
  // 50-state + DC consent rules (one_party vs two_party), statute citations,
  // and default announcement text (EN + ES). Read-mostly; seeded on deploy
  // and mutated only by platform-admin when state laws change.
  callRecordingConsentMatrix: 'crm',
  // callRecordingRetentionConfig ('crr') — per-tenant cascade table keyed on
  // (companyId, phoneIdentityId) holding retentionDays, legalHoldEnabled,
  // deleteStrategy (soft|hard), and requireReasonOnAccess. NULL
  // phoneIdentityId = tenant-default row. Mirrors voicemailConfig shape.
  // Distinct from callRecording ('crc') which is the recorded-audio row.
  callRecordingRetentionConfig: 'crr',
  // callRecordingAccessLog ('cra') — per-tenant append-only audit of every
  // listen / download / export / forward event. E7 chain-of-custody evidence
  // — legal teams query this via `GET /:id/access-log` to prove who accessed
  // a held recording and why.
  callRecordingAccessLog: 'cra',
  smsThread: 'smt',
  smsMessage: 'smm',
  fax: 'fax',
  // Phase 18 (Fax Bidirectional) — per-tenant per-DID inbound fax routing rule.
  // The rule maps an inbound fax-capable phoneNumber to one (and optionally
  // multiple cc'd) email recipients + an optional SMS-notification phone.
  // `frr` (Fax Routing Rule) verified collision-free against the full prefix
  // registry before adoption; the `should have no duplicate prefixes` guard
  // test catches any future regression. Distinct from `fax` (the inbound /
  // outbound fax message itself), `frd` not used anywhere, and the Phase 17
  // SMS family `srm` (smsReputationMetric) / `tdb` (tenDlcBrand) /
  // `tdc` (tenDlcCampaign).
  faxRoutingRule: 'frr',
  cpaasAccount: 'cpa',
  tcpaConsent: 'tcc',
  dncEntry: 'dnc',
  portingRequest: 'prq',
  // ── Phase 22 — Porting Pipeline + Temp-Bridge DID ──
  // Two new entities power the FCC-compliant port-in / port-out lifecycle on
  // top of the Phase 2 `portingRequests` skeleton (graduated Phase 22 from
  // skeleton → wired implementation per UNIVERSAL §14P.10.B / §14P.11.H
  // MSL-001).
  //
  //   portTempBridge       → 'ptb'  (per-tenant `portInTempBridges` — tracks
  //                                   the auto-provisioned temp DID + the
  //                                   forward-target E.164 during the 10–45
  //                                   day NPAC port window. Status enum
  //                                   `active|deactivated|expired` with NULL-
  //                                   tolerant `(deactivatedAt > activatedAt
  //                                   OR deactivatedAt IS NULL)` CHECK
  //                                   constraint per §14P.12.A. Partial unique
  //                                   on `(portingRequestId) WHERE
  //                                   deactivatedAt IS NULL` per §14P.11.G —
  //                                   only one active bridge per port.)
  //
  //   portingRequestEvent  → 'pre'  (per-tenant `portingRequestEvents` —
  //                                   append-only timeline log keyed on
  //                                   `(portingRequestId, occurredAt)` with
  //                                   8 frozen `eventType` values:
  //                                   submitted / foa_received / foc_set /
  //                                   cutover_complete / rejected / cancelled /
  //                                   bridge_activated / bridge_deactivated.
  //                                   Consumed by the §14P.12.I evidence-export
  //                                   endpoint to provide FCC discovery the
  //                                   full state-transition history for a
  //                                   subpoenaed port record.)
  //
  // Both prefixes verified collision-free against the full registry. `ptb` is
  // distinct from `pib` (phoneIdentityLocationBinding) — different second
  // character — and from every other `pXX` prefix (`pca`/`pin`/`pcl` patrol,
  // `pdy`/`pri`/`pys` payday, `pyr`/`pym`/`pap`/`pac`/`pdi` payroll,
  // `pse`/`psr`/`psv`/`pst` palm/prep). `pre` is distinct from `prc`
  // (passwordResetCode), `prk` (portRefreshToken), `prq` (portingRequest),
  // `prt` (pricingTier), `prl` (pricingRule), `prm` (permission), `prf`
  // (preference), `prg` (progress) — every existing `prX` prefix is checked.
  // The `should have no duplicate prefixes` guard test catches any future
  // regression. Per UNIVERSAL §2 #3 — recompute against the live registry at
  // kickoff (verified 2026-05-06: 90 → 92 cumulative).
  portTempBridge: 'ptb',
  portingRequestEvent: 'pre',
  // portingRequestDocument ('pqd') = PORT-IN-SUBMIT-001 S7: one per-tenant `portingRequestDocuments` row per
  // document a port-in carries to its carrier (the utility bill, the LOA), its sealed bytes in the porting-document
  // store. Measured free (the plan's `prd` is `product`); read as the porting request's (`prq`) document.
  portingRequestDocument: 'pqd',
  // Phase 23 (2026-05-07) — Reporting + Analytics. Cumulative 92 → 93.
  // `shr` is the per-tenant `shiftRecommendations` row prefix (one row
  // per (locationId, dayOfWeek, hourOfDay, analysisWindowDays) gap
  // analysis emitted by `ShiftRecommendationJob` and consumed cross-app
  // by PlannerPuffin via `GET /v1/port/shift-recommendations`).
  // Verified collision-free against the full registry at kickoff:
  //   `shr` distinct from `shp` (shipment), `sht` (shippingTender),
  //   `shf` (shipFromAddress) — every existing `shX` prefix checked.
  shiftRecommendation: 'shr',
  e911Registration: 'eni',
  stirShakenCert: 'stc',
  sipDevice: 'sip',
  // SEAT-001 (Phase 3) — per-user seat / extension primitive. `userSeat → 'sea'`
  // (the spec named `set`, but `set` is already the `setting` prefix above —
  // `sea` is collision-free against the full registry: distinct from every
  // existing `seX`/`sX` prefix). One `user_seats` row per (companyId, userId);
  // owns the canonical extension + personal DID + voicemail + ring rules +
  // presence; the SCIM (P15) provisioning target.
  userSeat: 'sea',
  mailboxHolderSub: 'mhs',
  // Phase 24 (Mailbox-Holder SKU) — two new per-tenant tables alongside
  // the Phase 2 mailboxHolderSubscriptions skeleton (which graduates in
  // the same PR per UNIVERSAL §14P.10.B + §14P.11.H MSL-001 precedent).
  //
  //   mailboxHolderRoutingRule → 'mhr' (Mailbox Holder Routing rule —
  //     per-holder rule binding to one of `forward_to_cell`, `cell_and_vm`,
  //     `vm_only`, `off`, with optional `activeHours` JSONB schedule;
  //     partial unique index on `(mailboxHolderSubId) WHERE deletedAt IS
  //     NULL` enforcing at-most-one active rule per holder via the
  //     Phase 20 §14P.11.G NULLS DISTINCT pattern).
  //   mailboxHolderBillingEvent → 'mhb' (Mailbox Holder Billing event —
  //     append-only Stripe Connect webhook event log keyed on
  //     `stripeEventId` UNIQUE so duplicate webhooks deterministically
  //     replay-of-existing-row; carries `tenantShareCents` +
  //     `platformShareCents` for revenue-share transparency + 7-year IRS
  //     retention horizon via `port.mailbox_holder.billing_event_retention_days`
  //     flag + `MailboxBillingEventRetentionGcJob` daily 06:30 UTC sweep).
  //
  // Both prefixes verified collision-free against the full registry —
  // the only registered `mh*` neighbour is `mhs` (mailboxHolderSub).
  mailboxHolderRoutingRule: 'mhr',
  mailboxHolderBillingEvent: 'mhb',
  // TOOLING-AND-DOCS-GEMS Q-TDG-13 (owner ruling "Build a plan catalog", 2026-09-30) —
  // `mailboxHolderPlan → 'mhp'` (Mailbox Holder Plan): one row per tenant-defined holder
  // plan (name, tier, monthly price) in `mailboxHolderPlans`, carrying the Stripe Price the
  // server created on the tenant's Connected Account. Referenced by
  // `mailboxHolderSubscriptions.mailboxHolderPlanId`. Collision-free: the registered `mh*`
  // neighbours are `mhs`, `mhr` and `mhb`.
  mailboxHolderPlan: 'mhp',
  callIntentTag: 'cit',
  numberInventoryItem: 'nui',
  outboundCampaign: 'ocp',
  outboundCampaignAttempt: 'oca',
  // Phase 19 (Outbound Transactional) — per-tenant outboundTemplates table.
  // Handlebars-rendered SMS / SSML message templates keyed on (companyId,
  // phoneIdentityId, triggerTypeKey, channel) with per-brand → tenant-default
  // cascade. FK from outboundCampaigns.outboundTemplateId + from
  // outboundCampaignAttempts.outboundTemplateId. The companion taxonomy
  // table (public.outboundTriggerTypes) uses a natural VARCHAR(50) PK on
  // triggerTypeKey, not a prefixed ID, per the canonical reference-table
  // pattern (cf. Phase 11 callRecordingConsentMatrix.stateCode); EPI-001
  // exempt by intent — debuggability is better with `'package_ready'` than
  // `oti_<uuid>`. `otp` (outboundTemplate) verified collision-free against
  // the full registry before adoption — distinct from sibling outbound
  // prefixes `ocp` (outboundCampaign) + `oca` (outboundCampaignAttempt).
  outboundTemplate: 'otp',
  // Phase 20 (TCPA + DNC + Consent + Audit) — two new compliance entities.
  //
  //   consentCaptureForm → 'cfm'  (Consent ForM — per-tenant authoring of
  //     reusable TCPA disclosure forms; channels JSONB array (voice|sms|fax),
  //     source enum, status (active|archived), soft-delete via deletedAt).
  //     The Phase 20 spec originally proposed `ccf` but live-registry check
  //     (per UNIVERSAL §2 #3 "Recompute against the live registry at kickoff")
  //     found `ccf` already registered to `carrierConfig` (line 112 of this
  //     file). Following the Phase 7/8 prefix-collision precedent (Phase 7
  //     `prt`→`prq` for portingRequest after a `pricingTier` collision; Phase 8
  //     `ivn`/`ivt`→`ivr`/`ino`/`itn` after `inventory`/`invitation` collisions),
  //     swapped to `cfm` — collision-free, semantically clear, distinct from
  //     `cfg` / `cnf` / `csf` / `tcc` (tcpaConsent). The
  //     `should have no duplicate prefixes` guard test catches future regressions.
  //
  //   dncRegistrySync → 'drs'  (Dnc Registry Sync — per-tenant lifecycle
  //     log row written by DncFederalRegistrySyncJob on each sync attempt;
  //     records started/completed/failed status, addedCount, removedCount,
  //     errorMessage. Also written by per-state sync adapters (`registry`
  //     column distinguishes federal_registry vs state_tx / state_pa / etc. —
  //     the states `@puffin-core/dnc-registry-law` names).
  //     `drs` verified collision-free against the full registry before adoption.
  consentCaptureForm: 'cfm',
  dncRegistrySync: 'drs',
  // ── Phase 21 — E911 + STIR/SHAKEN + Call Quality Monitoring ──
  // Phase 21 graduates two Phase 2 skeletons (`E911Registration` → `eni`,
  // `StirShakenCert` → `stc`) from skeleton to full lifecycle, but adds
  // exactly ONE new entity prefix:
  //
  //   callQualityMetric → 'cqm'  (per-tenant `callQualityMetrics` — flat
  //                                row per CPaaS-callback-ingested quality
  //                                sample with mos / jitterMs / packetLossPercent
  //                                / codec / cpaasAccountId / direction /
  //                                endpointType. Append-only — no UPDATE /
  //                                softDelete on the model. Aggregated by
  //                                `aggregateByCarrier` for the per-cpaasAccount
  //                                admin dashboard. Consumed in-memory by the
  //                                `CallQualityMonitorService` for EWMA-based
  //                                N-consecutive-low-MOS alerting; the table
  //                                itself is the durable timeline, the in-memory
  //                                state is just the alert detector.)
  //
  // The prefix was verified collision-free against the full registry before
  // adoption. Distinct from `cqu` (callQueue) / `cqe` (callQueueEntry) —
  // different third character. Distinct from `cev` (callEvent), `cit`
  // (callIntentTag), `cll` (call), `crc` (callRecording), `cra`
  // (callRecordingAccessLog), `crm` / `crr` (Phase 11 recording family),
  // `cet` (callEmergencyTrigger), `ctr` (callTranscript) — every existing
  // `c**` prefix is checked. The `should have no duplicate prefixes` guard
  // test catches any future regression.
  callQualityMetric: 'cqm',
  // Phase 26 (2026-05-11): Platform Admin Console — three new public-schema
  // tables backing the cross-tenant operator surface at
  // console.portpuffin.com. All three identifiers are checked for collisions
  // against every existing entry — `pan` (platformAdminNote) is distinct from
  // `pal` (platformAuditLog) and `pab` (portAuditLog) by third character;
  // `tpt` (tenantPlanTier) is distinct from `tum` (ttsUsageMeter) and `tdb`
  // (tenDlcBrand) by third character; `abu` (abuseAlert) has no prior `ab*`
  // family. The `should have no duplicate prefixes` guard test catches any
  // future regression.
  platformAdminNote: 'pan',
  tenantPlanTier: 'tpt',
  abuseAlert: 'abu',
  // Platform co-sign challenge — the single-use, time-bound token a second platform admin must
  // present to confirm a high-risk platform action (a feature-flag transition, a staff
  // deactivation). Stored in Valkey with a TTL. It was `featureFlagCoSignChallenge` (`ffc`) until
  // L-R7CLOSE row 33 generalised the challenge beyond flags; a challenge lives minutes, so no stored
  // `ffc_` id outlives the rename.
  // EPHEMERAL (EPHEMERAL_ID_TYPES): a platform co-sign challenge, held only in the co-sign cache with a TTL by
  // @puffin-core/middleware's PlatformCoSignChallengeStore, never a database row.
  platformCoSignChallenge: 'pcg',
  // Phase 5: Webhook signatures + call state machine.
  // webhookIdempotencyEntry ('wid') — per-tenant row that enforces at-most-once
  // processing of CPaaS webhooks. Populated on first receipt via the idempotency
  // middleware; GC'd by WebhookIdempotencyGcJob once expiresAt passes.
  webhookIdempotencyEntry: 'wid',
  // E9: Durable audit-log persistence (public.portAuditLogs).
  // portAuditLog ('pab', "port audit blob") — every state-mutating operation in
  // port-api emits one row via emitAuditEvent(). Independent prefix from the
  // pos-api 'pal' (platformAuditLog) to avoid cross-app shape ambiguity in logs
  // and traces; the two tables have different columns + retention semantics.
  portAuditLog: 'pab',
  // Phase 3: Auth infrastructure (public schema tables).
  // `prt` was master-planned for portRefreshToken but was already taken by
  // `pricingTier` (registered earlier). `prk` (port refresh key) is the
  // collision-free replacement — same pattern as the Phase 2 `prt`→`prq`
  // resolution for portingRequest.
  portRefreshToken: 'prk',
  portConsumedJti: 'pcj',
  passwordResetCode: 'prc',
  // PROFILE-003 Phase 2C (2026-06-03): verified email-change request rows in
  // the per-tenant `emailChangeRequests` table. A user-initiated email change
  // mints one of these (token → sha256 hash → pending row), confirmed via a
  // link sent to the NEW address. Mirrors the password-reset token pattern.
  emailChangeRequest: 'ecr',
  // REPAIR-LEDGER-AND-TYPED-ID-001 RLT-Q40 (b) (2026-09-29): a PENDING storefront sign-up in
  // `public.purchase_customer_signups` — the emailed link's row (token → sha256 hash), consumed when
  // its recipient sets a password. Same pattern as `emailChangeRequest`; `sgn` was unregistered.
  storefrontSignup: 'sgn',
  // LIFE-SHORTLISTED-001, the shopper privacy surface (owner ruling G6, 2026-10-07): a PENDING guest erasure
  // request in `public.purchase_guest_erasure_links` — the emailed link's row (token → sha256 hash), consumed
  // when the person who owns the address confirms. Same pattern as `storefrontSignup`; `gel` was unregistered.
  guestErasureLink: 'gel',
  // DTOWNRETAIL-SITE-001 U10-a: a PENDING mailbox-holder privacy request in `public.port_holder_privacy_links`
  // — the holder portal's guest intake, the same row shape as `guestErasureLink`; `hpl` was unregistered.
  holderPrivacyLink: 'hpl',
  rateLimitEntry: 'rle',
  // Phase 7 enterprise hardening (Stage 12): HTTP request idempotency entries.
  // Populated by the shared `@puffin-core/middleware` idempotencyKey middleware
  // on first receipt of an `Idempotency-Key` header; replay requests match on
  // the composite (companyId, userId, idempotencyKey, method, path) and
  // return the stored response body. Per-tenant table (not public).
  requestIdempotencyEntry: 'rie',
  // Phase 8 (Inbound Routing + TwiML/Call-Control IVR + Business-Hours Attendant):
  // tenant-authored IVR tree primitives. The Phase 8 spec originally proposed
  // `ivn` (ivrNode) and `ivt` (ivrTransition) but both were already registered —
  // `ivn` = inventory (line 99), `ivt` = invitation (line 70). Replacements:
  //   ivrTree       → 'ivr'  (IVR)
  //   ivrNode       → 'ino'  (IvR nOde)
  //   ivrTransition → 'itn'  (Ivr TraNsition)
  // The `should have no duplicate prefixes` guard test catches future
  // collisions before they ship — same retrospective as the Phase 3 `prt`
  // duplicate that motivated the v1.1 guard-test requirement.
  ivrTree: 'ivr',
  ivrNode: 'ino',
  ivrTransition: 'itn',
  // CALLFLOW-BUILDER-001 (PortPuffin Tenant Lifecycle Phase 12): AI-assisted
  // site-crawl onboarding seeds the AI receptionist's per-tenant knowledge base
  // from the tenant's website. Two tenant-scoped tables hold crawl provenance +
  // the embedded, retrievable chunks (vector(1024), reusing the help-embeddings
  // Voyage stack). `aks`/`akc` verified collision-free against the full registry
  // before adoption — the `should have no duplicate prefixes` guard catches any
  // future regression.
  //   knowledgeSource → 'aks'  (Ai Knowledge Source — `port_ai_knowledge_sources`)
  //   knowledgeChunk  → 'akc'  (Ai Knowledge Chunk  — `port_ai_knowledge_chunks`)
  knowledgeSource: 'aks',
  knowledgeChunk: 'akc',
  // Phase 9 (Endpoint Registration): the four call-receiving endpoint classes
  // are normalised through two tenant-scoped tables.
  //   softphoneSession → 'sps'  (soft-phone session — WebRTC client registration)
  //   endpointRoute    → 'erp'  (endpoint route — the opaque routing target id
  //                              referenced by Phase 8's `route_to_endpoint`
  //                              IVR node body; pattern `^erp_[0-9a-f-]{36}$`
  //                              is already locked by validateNodeBody.js:40).
  // `sps` + `erp` verified collision-free against the full 403-prefix registry
  // before adoption — the `should have no duplicate prefixes` guard test will
  // catch any future regression.
  softphoneSession: 'sps',
  endpointRoute: 'erp',
  // Phase 12a (Hold/Queue/Callback/Transfer/Blast-Ring): three tenant-scoped
  // entities power the queue + callback admin surfaces.
  //   callQueue       → 'cqu'  (call queue config — referenced by Phase 8's
  //                              `route_to_queue` IVR node body via the
  //                              `^cqu_[0-9a-f-]{36}$` pattern locked at
  //                              validateNodeBody.js:46)
  //   callQueueEntry  → 'cqe'  (live caller-in-queue + analytics row;
  //                              CAS-dequeued single-flight on `dequeuedAt
  //                              IS NULL` per §14E.7 hard-delete-CAS pattern)
  //   callbackRequest → 'cbr'  (deferred outbound callback intent;
  //                              dispatched by BullMQ worker honoring TCPA
  //                              consent gate from Phase 20 — Phase 12a
  //                              ships a stub returning `{allowed: true}`)
  // All three prefixes verified collision-free against the full registry
  // before adoption; the `should have no duplicate prefixes` guard test
  // catches any future regression.
  callQueue: 'cqu',
  callQueueEntry: 'cqe',
  callbackRequest: 'cbr',
  // UNREACHABLE-MODULE-BURNDOWN-001 L17 (Q-L17-1, the softphone conference bridge):
  //   callParticipant → 'cpt'  (one leg in a call's conference room — the caller, the
  //                             answering agent, a consult target — with the user on it and
  //                             when it joined and left; the participant check behind the
  //                             user-authenticated hold / transfer routes reads it)
  // `cpt` measured free before adoption: no key of ID_PREFIXES mapped to it.
  callParticipant: 'cpt',
  // LIFE-SHORTLISTED-001 U18d (owner rulings Q-LS-63 / Q-LS-65, outbound voice carrier failover):
  //   callerIdVerification → 'civ'  (one tenant number's caller-ID verification state on ONE
  //                                  carrier account that does not host it — `pending`,
  //                                  `verified` or `not_verified`; the outbound voice router may
  //                                  fail a call over to that account only while it reads
  //                                  `verified`. The verification CODE is never stored.)
  // `civ` measured free before adoption: no key of ID_PREFIXES mapped to it.
  callerIdVerification: 'civ',
  // LIFE-SHORTLISTED-001 U18e (owner rulings Q-LS-67 / Q-LS-71, SMS carrier failover from a backup
  // texting number):
  //   smsBackupNumber        → 'sbk'  (a brand's backup texting number on ANOTHER carrier — `pending`,
  //                                    `ready` or `not_ready`; the outbound SMS dispatcher fails a text
  //                                    over to it only while it reads `ready`, freshly checked)
  //   platformSmsNumberCheck → 'psn'  (`public.platformSmsNumberChecks`: a PLATFORM number's readiness
  //                                    verdict, keyed by the number)
  //   platformSmsOptOut      → 'pop'  (`public.platformSmsOptOuts`: the platform's own cross-carrier
  //                                    opt-out list)
  // All three measured free before adoption. The plan's first picks for the platform pair, `psc` and
  // `pso`, were NOT (planScenario, prospectSource). 🔴 The check's first registered prefix, `pnc`, was free on
  // its own base and NOT on the joined tree: REGISTER-PRICER-AND-STOCK's `productLocationNotCarried` took it in a
  // parallel lane, so the FULL-MERGE-1001 push-13 join held two owners for one prefix (and ENTITY_TYPE_BY_PREFIX
  // could answer only one). Re-picked as `psn`, measured free on the joined tree before any row was written.
  smsBackupNumber: 'sbk',
  platformSmsNumberCheck: 'psn',
  platformSmsOptOut: 'pop',
  // Phase 13 (ASR Streaming + Bilingual Auto-Detect): two tenant-scoped
  // entities power the streaming-ASR pipeline that wakes up the AI
  // receptionist.
  //   asrSession      → 'asr'  (per-call streaming session lifecycle —
  //                              status starting / active / ended / errored,
  //                              denormalised provenance for finance queries,
  //                              `port.asr.session_max_duration_seconds`
  //                              hard cap of 1800s matches the renderer's
  //                              `<Pause length="1800">`)
  //   callTranscript  → 'ctr'  (append-only per-utterance log; `text` is
  //                              column-encrypted AES-256-GCM ciphertext per
  //                              §14E.1, decrypted lazily on read with a
  //                              process-wide LRU cache keyed by
  //                              `encryptionKeyRef`. Ships with `legalHold`
  //                              + supporting columns + CAS-gated `hardDelete`
  //                              method per §14E.7 contract uniformity even
  //                              though the admin route is deferred to a
  //                              future phase.)
  // Both prefixes verified collision-free against the full registry; the
  // `should have no duplicate prefixes` guard test catches any future
  // regression. Distinct from `asn` (analyticsSession), `asg` (assignment),
  // `cit` (callIntentTag), `cev` (callEvent), `cll` (call), `crc`
  // (callRecording), `crm` / `crr` / `cra` (Phase 11 recording family).
  asrSession: 'asr',
  callTranscript: 'ctr',

  // Phase 12b (Emergency Keyword Detection — deferred build that lands AFTER
  // Phase 13 ASR + Phase 10 voicemail transcription) adds one tenant-scoped
  // entity:
  //   callEmergencyTrigger → 'cet'  (per-tenant audit row created when an ASR
  //                                   partial transcript or a Phase 10
  //                                   voicemail transcription contains a
  //                                   configured keyword above the
  //                                   per-tenant confidence threshold.
  //                                   Stores the verbatim matched phrase for
  //                                   compliance + the cross-app dispatch
  //                                   lifecycle column `palmBroadcastStatus`
  //                                   in `('pending','dispatched','failed','suppressed')`.
  //                                   Mirrors Phase 11's `cra` createOnly
  //                                   audit-log shape — no UPDATE / softDelete
  //                                   exposed; lifecycle columns are mutated
  //                                   only via discrete CAS methods.)
  // The prefix was verified collision-free against the full registry before
  // adoption. Distinct from `cev` (callEvent), `crt` (certificate), `cdr`
  // (cashDrawer), `cev` (callEvent), `cit` (callIntentTag), `cll` (call),
  // `crc` (callRecording), `cra` (callRecordingAccessLog), `crm` / `crr`
  // (Phase 11 recording family). `cet` itself was not previously registered
  // to any entity type — the `should have no duplicate prefixes` guard test
  // catches any future regression.
  callEmergencyTrigger: 'cet',

  // Phase 14 (TTS Streaming + Voice Cloning) adds three entities powering
  // the outbound-audio side of the AI receptionist (mirror of Phase 13's
  // ASR streaming pipeline which powers the inbound-audio side):
  //   voiceCatalogEntry → 'vco'  (public.voiceCatalogEntries — global stock-
  //                                voice registry shared by every tenant;
  //                                seeded with the 20 Phase 6 frontend
  //                                voiceCatalog.js constants + 10 ElevenLabs
  //                                stock voices = 30 total. Deletes the
  //                                Phase 6 frontend constant atomically with
  //                                Phase 14's `useVoiceCatalog` hook
  //                                cutover — no transitional shim per Gen-0.)
  //   clonedVoice       → 'clv'  (per-tenant clonedVoices — voice-clone
  //                                submissions with rights attestation +
  //                                pending_training/training/ready/rejected/
  //                                disabled status machine; partial unique
  //                                on (companyId, phoneIdentityId) WHERE
  //                                status=ready guarantees at-most-one ready
  //                                clone per brand; ElevenLabs-only because
  //                                Polly does not support cloning)
  //   ttsUsageMeter     → 'tum'  (per-tenant ttsUsageMeters — month-bucketed
  //                                cost rollup with denormalised
  //                                ttsModel/synthesizePromptVersion provenance
  //                                per §14D.3, atomic ON CONFLICT increment
  //                                via composite unique
  //                                (companyId, periodStart, provider,
  //                                ttsModel) so concurrent synthesize calls
  //                                from a single port-api instance never lose
  //                                an increment)
  // All three prefixes verified collision-free against the full registry;
  // the `should have no duplicate prefixes` guard test catches any future
  // regression. Distinct from `val` (voiceAuditLog), `vtl` (voiceTemplate),
  // `vpc` (voicePlatformConfig), `vtc` (voiceTenantConfig), `vml`
  // (voicemail), `vrc` (voicemailConfig) — every existing voice* prefix is
  // checked. `tum` is not previously registered to any entity.
  voiceCatalogEntry: 'vco',
  clonedVoice: 'clv',
  ttsUsageMeter: 'tum',

  // Phase 15 (LLM Orchestration + Turn-Taking + Barge-In + Intent
  // Classification) adds four entities powering the AI receptionist's
  // conversational state machine + cost metering:
  //   aiAgentConfig       → 'agt' (per-tenant aiAgentConfigs — reusable
  //                                AI receptionist persona / safety / cost
  //                                config. Reverse-pointer to phoneIdentity
  //                                via nullable `phoneIdentityId` column +
  //                                partial-unique index so at most one
  //                                config is the brand-default per
  //                                (companyId, phoneIdentityId). Also
  //                                referenced from Phase 9
  //                                `endpointRoutes.targetDescriptor.agentConfigId`
  //                                and Phase 12a
  //                                `callQueues.overflowAiAgentConfigId`.
  //                                Customizations JSONB column carries
  //                                tenant safe-tweaks (greeting override,
  //                                brand persona tone, additional context)
  //                                that PromptBuilder applies on top of the
  //                                4 LOCKED safety-rail sections.)
  //   aiConversation      → 'aic' (per-tenant aiConversations — one row per
  //                                AI-receptionist call lifecycle. UNIQUE
  //                                (companyId, callId) — exactly one
  //                                conversation per call. Status state
  //                                machine: active → ended | handed_off |
  //                                errored. Carries denormalised LLM
  //                                provenance + cost rollup +
  //                                §14D.2 correlationId.)
  //   aiConversationTurn  → 'act' (per-tenant aiConversationTurns — append-
  //                                only per-turn log. UNIQUE (aiConversationId,
  //                                turnIndex) makes append-only the DB
  //                                invariant. content column is column-level
  //                                AES-256-GCM encrypted ciphertext per
  //                                §14E.1 / §14G.2 sync-encrypt pattern.
  //                                Tool-call surface persists
  //                                toolCallName/Input/Output for Phase 16
  //                                vertical AI skills; tool turns may have
  //                                NULL content because structured data
  //                                lives in JSONB columns.)
  //   aiUsageMeter        → 'aum' (per-tenant aiUsageMeters — period-
  //                                bucketed cost rollup with denormalised
  //                                llmProvider/llmModel provenance per
  //                                §14D.3, atomic ON CONFLICT increment
  //                                via composite unique (companyId,
  //                                periodStart, llmModel) so concurrent
  //                                orchestrator turn-persists never lose
  //                                an increment. Default 5-minute cron
  //                                bucket.)
  // All four prefixes verified collision-free against the full registry;
  // the `should have no duplicate prefixes` guard test catches any future
  // regression. Distinct from `aac` (unregistered), `agc` (unregistered),
  // `aip` (unregistered) — `agt` was checked because Phase 9's
  // `endpointRoutes.targetDescriptor.agentConfigId: 'agt_<uuid>'` string
  // contract (shipped 2026-04-22) implicitly reserved the prefix without
  // registering it; Phase 15 closes the loop. `act` checked against `acc`
  // (accounting?), `aco` (unregistered), `acl` (unregistered) — no
  // collision. `aic` and `aum` are not previously registered to any
  // entity. `agt` accordingly raises PortPuffin's plan-scoped cumulative
  // count from 76 → 80, one above the spec's stated 79 — the spec did
  // not list aiAgentConfig as a separate table; design decision D1 in
  // the Phase 15 plan adopts the Phase 11
  // `callRecordingRetentionConfigs` precedent (separate table + reverse-
  // pointer pattern) for cleaner Phase 9 wiring + 1:N reuse semantics.
  aiAgentConfig: 'agt',
  aiConversation: 'aic',
  aiConversationTurn: 'act',
  aiUsageMeter: 'aum',

  // Phase 08 — AI Receptionist Create Flow + Self-Enablement (AI-RX-SELFSERVE-001).
  //   aiReceptionistEnablement → 'aie' (per-tenant aiReceptionistEnablements —
  //                                a singleton-per-tenant durable on/off state
  //                                for the AI receptionist. UNIQUE (companyId)
  //                                = one row per tenant. The runtime gate
  //                                (AsrOrchestrator.start +
  //                                InboundRoutingService._resolveAsrContext)
  //                                ANDs this `enabled` state behind the
  //                                `port.asr.enabled` tier entitlement, so a
  //                                tenant can pause/resume their AI receptionist
  //                                without editing their IVR. Default OFF for
  //                                new tenants; the AI-RX-SELFSERVE-001 migration
  //                                seeds `enabled=true` for tenants who already
  //                                have an active `aiAgentConfigs` row so the
  //                                live ProtectorPuffin receptionist is not
  //                                interrupted.) Verified collision-free against
  //                                the full registry (`aie` was unregistered;
  //                                distinct from `aic`/`act`/`aum`/`agt`).
  aiReceptionistEnablement: 'aie',

  // ── Phase 16 — Vertical AI Skills (PortPuffin + ParcelPuffin) ──
  // Two prefixes complete the skill-registry seam shipped by Phase 15
  // closure pass (`AiToolRegistry`):
  //
  //   aiSkillInvocation → 'asi' (per-tenant `aiSkillInvocations` —
  //                              append-only invocation log capturing every
  //                              `AiToolRegistry.invoke(name, input, ctx)`
  //                              call. One row per orchestrator tool dispatch
  //                              with input/output/success/latencyMs/
  //                              errorMessage. Keyset-paginated by
  //                              `created_at DESC` for the admin-history view.
  //                              Phase 23 retention sweeper trims the table
  //                              with a `(created_at)`-only index.)
  //
  //   appointment       → 'apt' (per-tenant `appointments` — pos-api-owned
  //                              customer-facing service-event records.
  //                              Created by the `BookAppointmentSkill` via
  //                              the cross-app `_internal/appointments`
  //                              route. Types: notary, mailbox-setup,
  //                              consultation, other. Collision-protected via
  //                              `tstzrange &&` overlap CHECK on each
  //                              transactional create. Idempotency-key
  //                              dedup via partial unique index.)
  //
  // Both prefixes verified collision-free against the full registry. `asi`
  // distinct from `asr` (asrSession). `apt` does not collide with any
  // existing registration. The `should have no duplicate prefixes` guard
  // test catches any future regression. Per UNIVERSAL §2 #3 — recompute
  // against the live registry at kickoff to absorb any Phase 23 / Phase 17
  // additions that may land in parallel.
  aiSkillInvocation: 'asi',
  appointment: 'apt',

  // ── PORT-AI-SKILLS-001 — AI receptionist call messages ──
  //   aiCallMessage → 'acm' (per-tenant `ai_call_messages` — durable record
  //                          of a message / callback request / insurance-quote
  //                          interest captured by an AI-receptionist action
  //                          skill (TakeMessageSkill / ScheduleCallbackSkill /
  //                          RequestInsuranceQuoteSkill). One row per captured
  //                          message; email/SMS owner-notification delivery
  //                          recorded inline. Read by the port-web admin inbox.)
  // Collision-free vs the full registry (distinct from `asi`/`apt`/`asr`/`aic`).
  aiCallMessage: 'acm',

  // ── Onboarding Platform (archive/completed-2026/onboarding-platform/) ──
  // Fifteen platform-wide tables powering CMS-driven onboarding across the ecosystem
  // (initial consumer: PlannerPuffin; factory-first design for all sibling apps). All
  // tables live in the `public` schema because onboarding content is platform-uniform
  // (no per-tenant overrides, per user direction).
  //
  // Three proposed prefixes collided with existing registrations at Phase 1 start and
  // were renamed — the `should have no duplicate prefixes` guard test above catches
  // future regressions:
  //   - `pst` (was) → `ost` — collided with `palmSetting` (line 412)
  //   - `ocp` (was) → `oph` — collided with `outboundCampaign` (line 589)
  //   - `oal` (was) → `oau` — collided with `oauthAuditLog` (line 208)
  //
  // Registry / validation tables (CI-populated; enforce code ↔ CMS consistency):
  // `publicOnboardingAppScopes` — the CANONICAL_APP_SCOPES slugs (9 at the
  // 2026-08-26 measure; recount from @puffin-core/onboarding, never this comment).
  onboardingAppScope: 'psa',
  onboardingSurfaceType: 'ost', // `publicOnboardingSurfaceTypes` — 6 surface types (checklist, coach_mark, ...)
  onboardingTourIdRegistryEntry: 'tie', // `publicOnboardingTourIdRegistry` — every `data-tour-id=` found in source
  onboardingEventRegistryEntry: 'ere', // `publicOnboardingEventRegistry` — every `fireTourEvent(…)` call site
  onboardingStuckAlert: 'osk', // `publicOnboardingStuckAlertHistory` — stuck-user alert rate-limit ledger (SCALE-JOB-001 W8-C)
  // Content definition tables (authored by platform admins via console CMS):
  onboardingChecklistDefinition: 'ocd', // `publicOnboardingChecklistDefinitions` — top-level checklist (5 phases for plannerpuffin)
  onboardingChecklistPhase: 'oph', // `publicOnboardingChecklistPhases` — phase within a checklist
  onboardingChecklistSubItem: 'osi', // `publicOnboardingChecklistSubItems` — sub-item within a phase
  onboardingCoachMarkDefinition: 'ocm', // `publicOnboardingCoachMarkDefinitions` — contextual overlay
  onboardingEmptyStateDefinition: 'oes', // `publicOnboardingEmptyStateDefinitions` — per-page empty-state content
  onboardingMilestoneDefinition: 'oms', // `publicOnboardingMilestoneDefinitions` — celebration moment
  onboardingHelpArticleDefinition: 'oha', // `publicOnboardingHelpArticleDefinitions` — slide-out panel content
  // HELP-002 Phase 3 (2026-05-21) — immutable per-publish snapshot of a
  // help-article body + brief + metadata, taken at the moment of save in
  // the authoring console (Phase 15). The version-history page diffs
  // adjacent rows and lets a platform-admin restore a prior version.
  onboardingHelpArticleVersion: 'ohv', // `publicOnboardingHelpArticleVersions`
  // HELP-002 Phase 3 (2026-05-21) — per-(articleId, userId) helpful/not-helpful
  // ballot row backing the inline `<HelpArticleFeedback>` widget at the
  // bottom of every article body. UNIQUE on (articleId, userId) for
  // last-write-wins idempotency.
  onboardingHelpArticleVote: 'ohf', // `publicOnboardingHelpArticleVotes`
  // HELP-002 Phase 14.5 (2026-05-22) — synonym row (per-appScope canonical
  // term + expansions array) backing the `/suggest` endpoint's
  // synonym-expansion path. Composite-unique on (appScope, term).
  onboardingHelpSearchSynonym: 'ohs', // `publicOnboardingHelpSearchSynonyms`
  // MUTATION-INSTRUMENT-001 (owner ruling 2026-09-25, "Changeable, latest wins") — ONE served AI
  // help answer (or refusal). PROVENANCE-ONLY, like `calendarRecast`: no table carries it as a key.
  // `HelpAiService.answer()` mints it on every envelope; it rides the answer's usage record,
  // the answer's `help.search.*` event and every `help.ai.feedback` rating of it, so a CHANGED
  // rating supersedes the earlier one and readers take the latest per answer.
  helpAiAnswer: 'hai',
  onboardingInlineHintDefinition: 'ohi', // `publicOnboardingInlineHintDefinitions` — permanent helper text
  // i18n + observability + audit:
  onboardingLocalizedString: 'ols', // `publicOnboardingLocalizedStrings` — polymorphic (ownerType + ownerId + stringKey + locale)
  onboardingAuditLogEntry: 'oau', // `publicOnboardingAuditLog` — every CMS mutation + support-ops read
  onboardingEventLogEntry: 'oel', // `publicOnboardingEventLog` — every user-surface interaction (monthly-partitioned, 13-mo retention)
  // Per-user progress (added Phase 1 after correction to Phase 0 assumption —
  // the draft ADR had progress on `public.userPreferences`, but discovery at
  // migration-writing time revealed the real `user_preferences` table lives
  // in per-tenant `cmp_*` schemas as legacy snake_case. Clean Gen 0 fix: give
  // onboarding its own public-schema progress table so every onboarding
  // primitive lives in `public` and support-ops cross-tenant queries stay
  // single-schema).
  onboardingUserProgress: 'oup', // `publicOnboardingUserProgress` — one row per (userId, companyId, appScope)
  // Phase 22 support-ops additions. Collision-audited 2026-04-24.
  onboardingStuckUserEntry: 'sue', // `public.onboardingStuckUsers` — materialized detection-job output
  onboardingStuckAlertHistory: 'sah', // `public.onboardingStuckAlertHistory` — append-only escalation ledger

  // ── TSP-001 (Tenant Setup Progress, 2026-05-11) ──
  // Three platform-wide tables splitting the legacy per-user
  // `public.onboardingUserProgress` ('oup') into one tenant-scoped checklist
  // table and one user-scoped tour-state table, plus an append-only
  // attribution feed surfaced in the new `/admin/setup-activity` page.
  // Decision: workspace setup is a TEAM artifact (anyone in the tenant who
  // performs the underlying domain action — saving the calendar, creating the
  // first function — advances the team's checklist via the realtime fanout).
  // Personal product tour state (coach marks seen, milestones celebrated,
  // hide-permanently) stays per-user. See standard
  // `GUIDES/shared/standards/onboarding-tenant-vs-user-state.md`.
  //
  // All three prefixes verified collision-free against the full registry:
  //   - `tsp` distinct from `tsk` (task) / `tsc` (tenantSecurity) / `tst` (test)
  //   - `utr` not previously registered to any entity
  //   - `tsa` distinct from `tas` (trainingAssignment) / `tat` (timecardAttestation)
  // The `should have no duplicate prefixes` guard test catches future regressions.
  tenantSetupProgress: 'tsp', // `public.tenantSetupProgress` — one row per (companyId, appScope)
  userTourState: 'utr', // `public.userTourState` — one row per (userId, companyId, appScope)
  tenantSetupActivityLog: 'tsa', // `public.tenantSetupActivityLog` — append-only attribution feed

  // ── Phase 17 (Inbound SMS + 2-way Threads + A2P/10DLC Registration) ──
  //   tenDlcBrand          → 'tdb' (per-tenant `tenDlcBrands` — A2P/10DLC
  //                                  carrier brand-registration submission +
  //                                  status lifecycle. Twilio TrustHub
  //                                  BrandRegistration SID OR Telnyx
  //                                  Messaging Profile binding stored in
  //                                  cpaasBrandId. Partial-unique on
  //                                  (companyId, phoneIdentityId) WHERE
  //                                  status NOT IN ('rejected','suspended').)
  //   tenDlcCampaign       → 'tdc' (per-tenant `tenDlcCampaigns` — campaign
  //                                  registration with use-case + sample
  //                                  messages + opt-in flow disclosure.
  //                                  Partial-unique on (companyId,
  //                                  tenDlcBrandId, useCase) WHERE status
  //                                  NOT IN ('rejected','suspended').)
  //   smsReputationMetric  → 'srm' (per-tenant `smsReputationMetrics` —
  //                                  hourly rollup of delivery/failure/
  //                                  opt-out counts per phoneIdentity.
  //                                  Generated `deliveryRate` column +
  //                                  unique on (companyId, phoneIdentityId,
  //                                  periodStart) — one row per per-hour
  //                                  bucket per brand. Read by the admin
  //                                  reputation chart + the degradation
  //                                  alert path that emits
  //                                  SMS_REPUTATION_DEGRADED audit.)
  // All three prefixes verified collision-free against the full registry;
  // the `should have no duplicate prefixes` guard test catches future
  // regressions. `tdb` distinct from `tcc` (tcpaConsent) — different first
  // two chars. `tdc` is unregistered. `srm` distinct from `srv*`, `sip`,
  // `sps`, `sue`, `sah` — unregistered.
  tenDlcBrand: 'tdb',
  tenDlcCampaign: 'tdc',
  smsReputationMetric: 'srm',

  // Phase 7 of legal-content factory rollout — `<SupportPage>` helpdesk.
  // Cross-app shared `public.support_tickets` table; ID prefix `sup`
  // because `tkt` is taken by taskTemplate, `stk` by shiftToken.
  // Distinct from `srv*` / `sgs` / `sgu` / `srm` / `spl` first-two-char
  // prefixes (no collision with `sup`).
  supportTicket: 'sup',

  // ──────────────────────────────────────────────────────────────────
  // Marketing CMS (MCMS-001, Phase I of the marketing-website Gen 0
  // remediation, 2026-05-14). The blog's entities live under the
  // canonical `public.marketing*` schema and feed every Colony app's
  // marketing blog (parcel/port/planner/payday/palm/patrol/prep/
  // purchase). The console.parcelpuffin.com platform-admin console
  // is the authoring surface. The page/section builder's `mpg` / `msc`
  // / `mas` / `mls` were retired with its tables (LIFE-SHORTLISTED-001
  // Q-LS-12, 2026-09-28 — owner ruling "Prune it"); nothing mints them.
  // ──────────────────────────────────────────────────────────────────
  marketingBlogPost: 'mbp', // `public.marketingBlogPosts` — local mirror of PublishPuffin published_content
  marketingCategory: 'mkc', // `public.marketingBlogCategories` — per-app blog category (`mca` taken by configAssessment)
  marketingTag: 'mtg', // `public.marketingBlogTags` — per-app blog tag
  marketingAuditLogEntry: 'mau', // `public.marketingAuditLog` — every CMS mutation (lifecycle, publish, archive)
  marketingBlogRenderedPage: 'mbh', // BLOG-STD-001 `public.marketingBlogRenderedPages` — prerendered blog HTML per (appKey, routePath, locale)
  marketingBlogAssetManifest: 'mam', // BLOG-STD-001 `public.marketingBlogAssetManifests` — per-app build asset tags (PK is appKey; prefix reserved for registry completeness)
  blogSource: 'bso', // LIFE-SHORTLISTED-001 Q-LS-35 `public.blogSources` — the ONE registry of every blog the platform syncs (a product's or a company's), its PublishPuffin account and status
  ownerConsoleUser: 'ocu', // OWNER-CONSOLE-001 `public.ownerConsoleUsers` — read-mostly tenant-owner-console login principal (CA-001 isolated audience; first consumer: Goldmark Growth Advisory)

  // DEP-EPI5 (Stage 5) — EPI-001 canonical prefixes for pos-web entities
  // that were minting raw uuid; now generateId-backed. Collision-checked
  // against the full ecosystem registry (cpy/blg were taken by
  // contractorPayment/breakLog → cmy/bil chosen).
  company: 'cmy', // public.companies — core tenant/company entity
  // CAP-001 (2026-06-10) — the à-la-carte tenant capability catalog ("Chinese
  // menu"). One `public.tenantCapabilities` row per company holding the granted
  // capability set (JSONB array validated against @puffin-core/tenant-capabilities).
  // Orthogonal to plan-tier entitlements (`tpt` tenantPlanTier). `tcp` verified
  // collision-free against the full registry — distinct from `tpt`/`tfc`/`tsc`/
  // `tsp`/`tcc`/`tcl`; the `should have no duplicate prefixes` guard catches any
  // future regression.
  tenantCapability: 'tcp', // public.tenantCapabilities — per-tenant capability grant set
  billing: 'bil', // pos-web BillingModel — billing records
  // `emq` (emailQueue) was retired with `public.email_queue` (FULL-MERGE-1001 schema-origins,
  // `20261005140100`); the notification dispatcher's outbox is `pos_notification_queue`.

  // ── PortPuffin Tenant Lifecycle Phase 1 (TPP-001 / CPAAS-PROV-001) ──
  // Collision-checked against the full registry by the `should have no
  // duplicate prefixes` guard test.
  provisioningRun: 'tpr', // port-api `port_provisioning_runs` — one resumable tenant-phone provisioning attempt
  // ── PortPuffin Tenant Lifecycle Phase 6 (BRANDED-CID-001) ──
  // `bcr` collision-checked against the full registry (distinct from every
  // existing `bX`/`bcX` prefix). One `branded_caller_id_registrations` row per
  // phone identity — CNAM/RCD display registration + spam-remediation tracking.
  brandedCallerIdRegistration: 'bcr', // port-api `branded_caller_id_registrations`
  // ── PortPuffin Tenant Lifecycle Phase 10 (SETTINGS-TABS-001) ──
  // `ald` collision-checked against the full registry (distinct from `adl`
  // analyticsDaily, `adj` adjustment, `adv` authenticatorDevice, `alt` alert,
  // `alc` alertConfiguration). One `port_alert_destinations` row per
  // tenant-configured email/webhook destination for compliance/quality alerts.
  alertDestination: 'ald', // port-api `port_alert_destinations`
  // ── PortPuffin Tenant Lifecycle Phase 13 (CALL-ANALYTICS-001) ──
  // Two prefixes, both collision-checked against the full registry by the
  // `should have no duplicate prefixes` guard test. `anr` is distinct from
  // `adl` (analyticsDaily), `adj` (adjustment), `atv` (prospectActivity), `alt`
  // (alert), `ald` (alertDestination). `svy` is distinct from `svh`
  // (prospectServiceHistory) + every existing `s*` prefix.
  //   callAnalyticsRollup → 'anr' (per-tenant `callAnalyticsRollups` — the
  //                                pre-aggregated analytics rollup cache, one
  //                                row per (companyId, bucketStart, granularity,
  //                                dimension, dimensionValue). UPSERT against the
  //                                natural-key UNIQUE; averages derived at read.)
  //   csatSurvey          → 'svy' (per-tenant `port_csat_surveys` — one measured
  //                                post-call CSAT survey per call. SMS channel
  //                                wired; channel-agnostic model.)
  //   portCallSummary     → 'pcs' (per-tenant `port_call_summaries` — one
  //                                post-call AI summary + extracted action items
  //                                + QA-rubric score per call, POSTCALL-AI-001
  //                                Phase 14. Verified collision-free against the
  //                                full registry at kickoff (2026-06-14); the
  //                                `should have no duplicate prefixes` guard test
  //                                catches any future regression — `pcs` is
  //                                distinct from `pca`/`pcl`/`pcn`/`pcj`/`pcc`/
  //                                `pjt` siblings + every other prefix.)
  callAnalyticsRollup: 'anr', // port-api `callAnalyticsRollups`
  // PLN-DEPT-PLAN-001 / DEPT-FACT-001 (2026-09-08) — pos-api
  // `pos_sales_department_daily`: one row per (company, STORE, civil business day,
  // POS department) carrying net sales attributed to the department the register
  // rang, reconciled EXACTLY to the canonical `SUM(transactions.totalAmount)`.
  // Collision-checked against the full registry at kickoff: `psd` is distinct
  // from `anr` above (and the retired `prr`, whose table planner `20261006100990`
  // dropped on 2026-10-13) and from every existing `p**` prefix (`pct`, `pmd`,
  // `pmc`, `prd`, `prt`, `pvr`, `pae`, `pcs`, `pjt`…).
  posSalesDepartmentDaily: 'psd',
  // PLN-LY-TWO-GRAINS-001 P1 / DEPT-FACT-002 (2026-10-09) — pos-api's two 5-MINUTE sales
  // buckets, written in the SAME transaction as the daily fact above and reconciling to it
  // per store-day: `pos_sales_department_5min` (store × 5-minute `bucketStart` × POS
  // department; money exact, the department's ticket TOUCHES as its count) and
  // `pos_sales_store_5min` (store × bucket; the exact distinct ticket count). They are what
  // the planner's hourly weights read so a Monthly department's deposits never shape a day,
  // and what the POS Daily Sales report's hourly rows come from. Collision-checked against the
  // full registry on 2026-10-09: `psm` and `pbk` are distinct from `psd` / `pdp` here, from
  // `psb` (patrolSubscription) and `pst` (palmSetting), and from every other `p**` prefix (the
  // no-duplicate guard test proves it).
  posSalesDepartmentFiveMinute: 'psm',
  posSalesStoreFiveMinute: 'pbk',
  // PLANNER-CANVAS-ONE-GRADE-001 U7c / DEPT-FACT-003 (2026-10-13) — pos-api
  // `pos_sales_ticket_departments_5min`: store × 5-minute bucket × the ticket's DEPARTMENT SET
  // (its sorted department ids) → the sale tickets with exactly that set. What a reader with its
  // own Daily / Monthly grouping needs to count "tickets with a Daily line" exactly (the
  // department bucket counts touches, the store bucket every ticket). Collision-checked against
  // the registry on 2026-10-13: `ptd` is free (the no-duplicate guard test proves it).
  posSalesTicketDepartmentsFiveMinute: 'ptd',
  // SUMMARY-TENDER-PREAGG-001 (2026-10-22) — pos-api's summary tender split FACT, written one store-day at a time
  // by the nightly rollup so `GET /reports/summary` sums store-days instead of probing every sale's tenders:
  // `pos_tender_split_daily` (store × store business day × line × method — the split statement's own rows) and
  // `pos_tender_split_store_days` (its coverage: one row per written store-day, with the header basis the reader
  // reconciles against). Collision-checked against the full registry on 2026-10-22: `tsd` and `tss` are free (the
  // no-duplicate guard test proves it).
  posTenderSplitDaily: 'tsd',
  posTenderSplitStoreDay: 'tss',
  // PLN-DEPT-GRAIN-001 / DEPT-PROFILE-001 (2026-09-23) — pos-api
  // `pos_department_demand_profiles`: one row per (company, store-or-chain, POS
  // department) carrying the 52-week demand features (tickets per store-month,
  // ticket size, day-of-month concentration, anchor day, weekday shares) and the
  // Daily / Monthly grain suggestion the planner shows. Overwritten nightly by
  // the 04:00 profile job after the 03:00 fact. `pdp` is distinct from `psd`
  // above and from every other `p**` prefix (the no-duplicate guard test proves it).
  departmentDemandProfile: 'pdp',
  // PLN-AUD-0024 / 0025 — planner-api `planner_approval_escalations`. One
  // append-style row per escalation event (manual "Escalate" button OR the
  // nightly escalation-sweep) recording the level, the prior + next approver
  // tier (rolePriority), and the SLA deadline. The queue normalizers resolve a
  // request's CURRENT escalation level from the latest active row here.
  plannerApprovalEscalation: 'pae',
  csatSurvey: 'svy', // port-api `port_csat_surveys`
  portCallSummary: 'pcs', // port-api `port_call_summaries`
  // PORT-OPENAPI-001 (Tenant-Lifecycle Phase 16) — tenant open-API key. One
  // row per tenant-issued API key in the per-tenant `port_api_keys` table; the
  // raw `pk_…` key is shown once + only its sha256 `keyHash` persists. The
  // webhook-subscription entity reuses the existing `webhookSubscription → 'whs'`
  // prefix (no new prefix needed there).
  portApiKey: 'pak', // port-api `port_api_keys`
  // NUDGE-LINK-001 P3-G (2026-09-06) — one per-tenant `port_screen_pop_settings` row per
  // company: the tenant's POS screen-pop preferences (enabled · terminal scope · what shows).
  screenPopSetting: 'psp', // port-api `port_screen_pop_settings`
  // N165 (2026-10-09) — one per-tenant `port_staffing_model_settings` row per company: the business's
  // call-volume staffing model (the Erlang C shrinkage), per owner rulings "30% (Recommended)". `stm`, not `psm`:
  // `psm` is `posSalesDepartmentFiveMinute`'s (above), and the duplicate was caught by the no-duplicate guard before
  // the creating migration (port `20261006100850`) ran, so no row was ever written with it.
  staffingModelSetting: 'stm', // port-api `port_staffing_model_settings`

  // ── ProspectPuffin — Business Development & Prospecting (PROSPECT-001) ──
  // The tenant-scoped BD/CRM domain (@puffin-core/prospect-domain, per-tenant
  // `cmp_*` schema tables), fed by the pluggable prospect-connectors (Service
  // Fusion reactivation + permit/listing acquisition) and prospect-scoring.
  // All 11 prefixes verified collision-free against the full registry before
  // adoption; the `should have no duplicate prefixes` guard test catches any
  // future regression. `acc`/`con`/`ste`/`opp`/`atv`/`ter`/`svh`/`pso`/`rsg`/
  // `srn`/`pcc` are each distinct from every existing prefix (notably `cac`
  // customerAccount, `ctc` contactSubmission, `csg` customerSegment, `act`
  // [unused-but-reserved], `loc` location, `srt`/`src`/`srk`/`srm` sibling
  // `sr*` prefixes, `ptn`/`pca`/`pcl`/`pcn`/`pcj` sibling `p*` prefixes).
  prospectAccount: 'acc', // cmp_*.prospect_accounts — a prospect/customer company (carries segment)
  prospectContact: 'con', // cmp_*.prospect_contacts — a person at an account
  prospectSite: 'ste', // cmp_*.prospect_sites — a physical commercial location
  prospectOpportunity: 'opp', // cmp_*.prospect_opportunities — a deal/initiative on an account
  prospectActivity: 'atv', // cmp_*.prospect_activities — call/email/sms/visit/note (append-only)
  prospectTerritory: 'ter', // cmp_*.prospect_territories — market/region (Portland/Vancouver WA/Central OR)
  prospectServiceHistory: 'svh', // cmp_*.prospect_service_history — synced Service Fusion jobs/invoices (read-only mirror)
  prospectSource: 'pso', // cmp_*.prospect_sources — acquisition provenance (permit/listing/manual flag)
  reactivationSignal: 'rsg', // cmp_*.prospect_reactivation_signals — computed lapsed-account score
  acquisitionSignal: 'aqs', // cmp_*.prospect_acquisition_signals — computed candidate qualification (PROSPECT-ACQ-001)
  prospectSyncRun: 'srn', // cmp_*.prospect_sync_runs — one resumable connector sync attempt
  prospectConnection: 'pcc', // cmp_*.prospect_connections — per-tenant connector config (source, cadence, status)
  // PROSPECT-SRCMIG-001 R-157 (2026-09-27) — one credential KIND of a connection (a Service Fusion API
  // client, a web-app user). `pcr` measured free of the registry, distinct from `pcc` beside it.
  prospectConnectionCredential: 'pcr', // cmp_*.prospect_connection_credentials — where one kind's secret lives
  // Auth-infra (NOT a BD-domain entity): public.prospect_consumed_jtis — cross-app SSO
  // single-use JTI replay store (mirrors port's `portConsumedJti`; public, not per-tenant).
  prospectConsumedJti: 'pjt',
  // SCALE-JOB-001 W8-F (2026-08-08) — `public.prospectQualifyDailyUsage` row: the
  // per-tenant per-UTC-day paid-qualification spend counter (atomic
  // increment-and-check behind `maxQualifyPerDay`; platform spend-guard ledger,
  // NOT tenant business data). `pqu` is distinct from `pqt` (patrolQuote), `pjt`
  // (prospectConsumedJti) + every existing `p*` prefix.
  prospectQualifyUsage: 'pqu',
  // SCALE-ACTIVATE-001 C2 (2026-09-02) — `public.prospectDraftDailyUsage` row: the
  // per-tenant per-UTC-day AI draft-assist spend counter behind `maxDraftsPerDay`
  // (same atomic increment-and-check ledger shape as `pqu`; the previous
  // per-process Map multiplied the cap ×N instances). `pdu` is distinct from
  // `pqu`, `pjt`, `pdl`-class prefixes + every existing `p*` prefix.
  prospectDraftUsage: 'pdu',
  // ── PROSPECT-SRCMIG-001 P4 (2026-09-19) — the snapshot ledger + the ten stage tables
  // (PROSPECT-SNAP-001). All per-tenant `cmp_*` tables; every prefix measured free of the
  // registry before it was chosen (`s*` + the `sg*` family were unclaimed).
  // ── PROSPECT-SRCMIG-001 P10 (2026-09-20) — the migration ledger (PROSPECT-MIGR-001). Per-tenant
  // `cmp_*` tables in the SOURCE tenant's schema; every prefix measured free before it was chosen.
  migrationRun: 'mgr', // cmp_*.prospect_migration_runs — one migration of a snapshot into a target (one in progress per target tenant)
  migrationRecord: 'mgl', // cmp_*.prospect_migration_records — one planned source record, written BEFORE its create is sent
  dedupeDecision: 'mdd', // cmp_*.prospect_dedupe_decisions — one dedupe candidate pair and the decision taken on it
  snapshotRun: 'snr', // cmp_*.prospect_snapshot_runs — one resumable source snapshot (one in progress per connection)
  snapshotRecord: 'snp', // cmp_*.prospect_snapshot_records — one fetched source record of a run (append-only)
  snapshotAttachment: 'sna', // cmp_*.prospect_snapshot_attachments — one attachment surface seen by a run + its fetch state
  stageCustomer: 'sgc', // cmp_*.prospect_stage_customers — canonical staged customer (TRUNCATE + reload)
  stageAddress: 'sga', // cmp_*.prospect_stage_addresses — staged service / billing address
  stageCall: 'sgk', // cmp_*.prospect_stage_calls — staged call (the source's case / ticket)
  stageJob: 'sgj', // cmp_*.prospect_stage_jobs — staged job (a scheduled visit)
  stageOrder: 'sgo', // cmp_*.prospect_stage_orders — staged order / estimate / invoice head
  stageOrderLine: 'sgl', // cmp_*.prospect_stage_order_lines — staged order line with its cost COMPONENTS
  stagePayment: 'sgp', // cmp_*.prospect_stage_payments — staged payment (gateway auth never fetched)
  stageMembership: 'sgm', // cmp_*.prospect_stage_memberships — staged membership on a customer / address
  stageUnit: 'sge', // cmp_*.prospect_stage_units — staged customer equipment (a door, an opener)
  stageAttachment: 'sgf', // cmp_*.prospect_stage_attachments — the ledger's attachment row denormalized for export
  stageReference: 'sgr', // cmp_*.prospect_stage_reference — user / catalog item / lookup / plan / job-document template
  // PROSPECT-SRCMIG-001 (2026-09-29, the stage-wipe incident) — the durable log of every stage load and
  // purge, so what the stage holds, and who changed it when, is answerable from the database. `sgd`
  // (stage loaD) measured free of the registry before it was chosen.
  stageLoad: 'sgd', // cmp_*.prospect_stage_loads — one stage load or purge (append-only history)
  // SCALE-ACTIVATE-001 C2 (2026-09-02) — the per-PROCESS fallback identity a
  // replica stamps as `migration_imports.ownerInstanceId` when the
  // `puffin-inst:` heartbeat is not enabled (REDIS_URL unset ⇒ one instance).
  // `pxi` (process-instance) is distinct from `pin` (patrolInvoice), `prc`
  // (passwordResetCode) + every existing `p*` prefix.
  processInstance: 'pxi',

  // ── PortPuffin Tenant Lifecycle — Phase 15: Enterprise Admin (SSO-SCIM-001) ──
  // Both verified collision-free against the full registry before adoption; the
  // `should have no duplicate prefixes` guard test catches any future
  // regression. `sso`/`scm` are distinct from every existing prefix (notably
  // `ses` session, `set` setting, `sea` userSeat, `scl` scheduleComplianceLog,
  // and the sibling `s*`/`sc*` prefixes).
  ssoConnection: 'sso', // public.port_sso_connections — SAML/OIDC IdP connection config (global table, companyId-scoped row)
  scimToken: 'scm', // public.port_scim_tokens — SCIM v2 bearer token (sha-256 hashed at rest; global table, companyId-scoped row)

  // ── PortPuffin Tenant Lifecycle — Phase 17: Outcome-Based AI SKU (OUTCOME-SKU-001) ──
  // The metered billable-outcome ledger — one immutable row per resolution
  // (booking / package-status / general agentic action) captured from the P14
  // agentic layer, rated at capture, settled (or deferred) to the platform
  // billing rail. `oce` verified collision-free against the full registry (the
  // `should have no duplicate prefixes` guard test catches any regression):
  // distinct from every existing `oc*`/`oX*` prefix (notably `ord` order,
  // `out`/`oca`/`occ`/`ocr` siblings — none start `oce`).
  outcomeEvent: 'oce', // port-api `port_outcome_events` (per-tenant)
  // TRUST-POSTURE-001 (Phase 18) — per-tenant `port_toll_fraud_alerts`: a
  // toll-fraud anomaly detection (velocity / cost-proxy / unusual-destination
  // band trip) raised by the TollFraudAnomalyDetectorService. `tfa` verified
  // collision-free against the full registry (the `should have no duplicate
  // prefixes` guard test catches any regression) — no existing `tf*` prefix.
  tollFraudAlert: 'tfa', // port-api `port_toll_fraud_alerts` (per-tenant)
  // HARD-JWTS-002 (2026-07-10) — refresh-token family lineage (reuse detection).
  // One `rtf` id per session lineage: minted at login/MFA/dev-test/cross-app
  // exchange by `createRefreshTokenStore` (`@puffin-core/auth-routes`), carried
  // through every rotation on the `familyId` column of the 9 tenant
  // `public.<app>_refresh_tokens` tables + the pos/port platform tables. `rtf`
  // verified collision-free against the full registry (distinct from `rle`
  // rateLimitEntry, `rie` requestIdempotencyEntry, and every other `r*` prefix);
  // the `should have no duplicate prefixes` guard test catches any regression.
  refreshTokenFamily: 'rtf',
  // SCALE-JOB-001 W8-B (2026-08-08) — public.portAlertDispatchLedger: the
  // at-most-once dispatch-claim ledger for port-api's ledgerless alert
  // producers (compliance-alert monitor, STIR/SHAKEN cert-rotation reminders,
  // TCPA anomaly alerter). One `pad` id per claimed (alertKind, dedupeKey,
  // windowBucketStart) dispatch. `pad` verified collision-free against the
  // full registry (no existing `pad` — `pab`/`pac`/`pag`/`pal`/`pan`/`pap`/
  // `par`/`pay` are the nearest neighbours); the `should have no duplicate
  // prefixes` guard test catches any regression.
  portAlertDispatch: 'pad', // port-api `public.portAlertDispatchLedger`
  // HBT-001 (2026-08-12, ruling `Q-R3CH-5`) — public.portJobRunHeartbeats: the
  // UNCHAINED operational store that scheduled-job per-tick self-audits were
  // moved to, off the tamper-evident `portAuditLogs` chain (94.0 % of whose
  // 1,165,359 rows were heartbeats). One `jrh` id per job tick. `jrh` verified
  // collision-free against the full 548-entry registry (the only `j*` prefixes
  // are `job`/`jbr`/`jrc`); the `should have no duplicate prefixes` guard test
  // catches any regression.
  portJobRunHeartbeat: 'jrh', // port-api `public.portJobRunHeartbeats`
  // NUDGE-LINK-001 R-6 (2026-09-06) — cmp_*.privacy_requests: one row per GDPR
  // data-subject request (Art. 15 export / Art. 17 deletion), carrying the
  // lifecycle the shared `@puffin-core/privacy-requests` engine drives.
  // `prv` verified collision-free against the then-573-entry registry (the
  // nearest `pr*` neighbours are `prc`/`prd`/`pre`/`prf`/`prg`/`pri`/`prk`/
  // `prl`/`prm`/`prq`/`prr`/`prt`; the obvious `pvr` is TAKEN by
  // `productVariant`, which is exactly why this comment names the check).
  // The `should have no duplicate prefixes` guard test catches any regression.
  privacyRequest: 'prv', // <app>-api `cmp_*.privacy_requests`
  // SECURITY-AND-CONNECTORS-001 Track C (2026-09-29) — `cmp_*.connections`: one tenant connection to
  // an external account (QuickBooks Online, Xero, FreshBooks, the payroll processor, Plaid), held by
  // the ONE connector factory `@puffin-core/connections`. `cxn` verified collision-free against the
  // then-639-entry registry (no other `cx*` prefix exists).
  connection: 'cxn', // pos-api `cmp_*.connections` (the connector factory's store)
  // EPI-TYPE-RESTORATION-001 (2026-09-17) — the PostalMate-import entity types that had no
  // registry entry, so `generateId` could not mint them even though the importer knew exactly
  // which entity it was writing. Until this initiative they all arrived as `tgt_` (see the
  // tombstone in "Relations & Links" above).
  //
  // The first three ADOPT a prefix already chosen on 2026-02-10 by
  // `apps/pos-api/migrations/20260210000002_epi001_tenant_schema_uuid_to_varchar.js`
  // (`COLUMN_PREFIX_MAP`: shapeId→pks, mailNameId→mmn, forwardingId→fwd). Registering them here
  // finishes that decision rather than making a new one.
  packageShape: 'pks', // pos-api `cmp_*.package_shapes.shapeId`
  mailboxMailName: 'mmn', // pos-api `cmp_*.mailbox_mail_names.mailNameId`
  forwardingAddress: 'fwd', // pos-api `cmp_*.forwarding_addresses.forwardingId`
  //
  // The last two are genuinely NEW, and only because that February map is keyed on COLUMN NAME,
  // which cannot separate two tables that share one:
  //   - `addressId` is the PK of BOTH `customer_addresses` (→ `cad`, customerAddress) and
  //     `customer_ship_to_addresses` (973,866 rows, its own lifecycle) → `sta`.
  //   - `zoneId` is the PK of BOTH `carrier_zones` (→ `czn`, carrierZone) and `rate_zones` → `rzn`.
  // `sta` and `rzn` verified collision-free against the full registry; the `should have no
  // duplicate prefixes` guard test catches any regression.
  shipToAddress: 'sta', // pos-api `cmp_*.customer_ship_to_addresses.addressId`
  rateZone: 'rzn', // pos-api `cmp_*.rate_zones.zoneId`
  // EPI-TYPE-RESTORATION-001 Phase 4 — three entity types that live BaseModel subclasses already
  // DECLARED as their `entityType` while being absent from this registry. `BaseModel.create()`
  // calls `generateId(this.entityType)`, so the first insert through any of them would have thrown
  // `Unknown entity type`. Latent only because all three tables are empty in every tenant today.
  //
  // Each has a model AND a creating migration — unimplemented features, not dead code — and
  // `pm_entity_resolution` has a documented intended writer
  // (`CrossSourceResolutionService.enumerateProjectEntities`). Registered rather than exempted.
  //
  // ⚠️ The natural three-letter forms were all TAKEN: `prq` = portingRequest, `pmr` =
  // pmMigrationRecord, `fac` = financialAccount. `peq`/`per`/`fca` verified collision-free; the
  // `should have no duplicate prefixes` guard test catches any regression.
  printEquipment: 'peq', // pos-api `cmp_*.print_equipment.equipmentId`
  pmEntityResolution: 'per', // pos-api `cmp_*.pm_entity_resolution.resolutionId`
  forecastAccuracy: 'fca', // planner-api `cmp_*.planner_forecast_accuracy.accuracyId`
  // EPI-TYPE-RESTORATION-001 Phase 5 — the quarantine record the remint writes when a `tgt_` value
  // resolves to no row. Those rows are not bookkeeping: each one is a reference that was ALREADY
  // dangling before the remint touched anything, held for a human decision rather than an
  // automated guess (R10's ruling), and they are what let the Phase-6 conformance contract carry
  // zero blanket column exemptions. A human will query and cite them, so they get a real id.
  //
  // Its sibling `pm_epi_remint_log` deliberately gets NO entry: that table is keyed on the `oldId`
  // it replaces, which is already a minted entity id, so a surrogate would have been a second key
  // for a uniqueness the data already carries.
  // The table folded into `data_repair_ledger` and was dropped (RLT-002, pos Batch 419); every `edr_` id survives in
  // its ledger row's `payload.danglingId`, and the fold's reversal restores it, so the prefix stays registered.
  epiDanglingReference: 'edr', // formerly pos-api `cmp_*.pm_epi_dangling_reference.danglingId`
  // EPI-TYPE-RESTORATION-001 residuals (2026-09-19) — the referential-integrity repair log. One
  // row per repointed reference and per quarantined row across the whole FK programme, carrying
  // the OLD value, the NEW value, the DERIVATION that produced it, and for a deletion the entire
  // row as jsonb.
  //
  // It is separate from R10's `pm_r10_repair_log` rather than an extension of it because that log
  // is a closed record of a finished repair: appending a second, differently-shaped programme to
  // it would make "what did R10 do" unanswerable. It gets a real id for the same reason
  // `epiDanglingReference` did — a human will cite these rows when they ask why a row is gone.
  // Folded the same way (pos Batch 419): each `eri_` id lives on in its ledger row's `payload.repairId`.
  epiRepairRecord: 'eri', // formerly pos-api `cmp_*.pm_epi_ri_repair_log.repairId`
  // REPAIR-LEDGER-AND-TYPED-ID-001 (2026-09-25) — the ONE data-repair ledger primitive in
  // `@puffin-core/database` (`repairLedger.js`), replacing six ledgers in five shapes that only
  // one-off migrations wrote and nothing pruned. `dataRepairEntry` keys one recorded repair (a
  // remint pair, a repoint, a deleted row's payload, a rebuild, an open dangling reference);
  // `dataRepairRun` keys the PERMANENT per-run summary that outlives the 90-day reversal window.
  // The six ledgers fold into these in L2, after which the EPI-specific types above retire.
  dataRepairEntry: 'dre', // `cmp_*.data_repair_ledger.entryId`
  dataRepairRun: 'drr', // `cmp_*.data_repair_runs.runId`
  // EPI-TYPE-RESTORATION-001 Q-17 follow-on (2026-09-24) — two ids that were minted outside the
  // registry because no type existed for them. `att` is `attempt`, so the attachment takes `atc`;
  // `ofp` was free. The `should have no duplicate prefixes` guard test catches any regression.
  //
  // `attachment` — one uploaded binary: the bucket key segment `attachments/<companyId>/<id>` and
  // the signed-URL segment, persisted on the owning row (ProspectPuffin
  // `prospect_activities.attachmentsJson`). It was a bare `crypto.randomUUID()`.
  attachment: 'atc', // @puffin-core/file-storage `generateAttachmentId()`
  // `offlinePayment` — one card-present payment queued while the reader was offline, held in the
  // injected offline store and replayed with this id as the processor's idempotency key. It was
  // `op_<uuid>`, an unregistered prefix.
  offlinePayment: 'ofp', // @puffin-core/payment-terminal `queueOfflinePayment()`
  // RESTORE-AND-SHIPPING-ENGINE-001 §2 #3 follow-on (2026-10-10) — ids palm-native reaches that were minted outside
  // the registry because the phone had no Web Crypto (Hermes ships none; palm-native now installs it from expo-crypto).
  // `pin` is taken, so the push installation takes `psi`. A third, `errorNotification` (`ern`), was registered for
  // `@puffin-core/error-handling`'s `generateNotificationId()` and retired with it (N195, re-cut by
  // BRANCH-RECONCILIATION-001, 2026-10-16): that minter had no production caller, so no `ern_` id was ever stored.
  offlineQueueItem: 'oqi', // @puffin-core/offline-sync `OfflineQueue` — one queued request on the device
  pushInstallation: 'psi', // palm-native `pushBootstrap` — the device's push subscription `deviceId`
  // ECOSYSTEM-EMAIL-001 lane T (2026-10-09) — managed tenant mail, held by `@puffin-core/managed-mail`. A mail
  // domain is globally unique and a sign-in must find a domain's mail host before any tenant is known, so both
  // tables live in `public`, keyed by company. `mdn` and `mad` were measured free (no key held either); the
  // physical-mailbox family (`mbx`, `mbr`, `mli`, `mmn`, `mh*`) is ParcelPuffin's and is unrelated.
  mailDomain: 'mdn', // `public."mailDomains".mailDomainId` — one domain whose mail the platform manages
  mailAddress: 'mad', // `public."mailAddresses".mailAddressId` — one mailbox or forwarder at a mail domain
  // PRONTOPUFFIN-001 phase 2 (2026-10-11) — a person's LINK to an email account they read in ProntoPuffin: one row per
  // user and address in the tenant table `pronto_mailbox_links`, its password sealed (owner ruling Q-PP-7). `pmx` was
  // measured free (no key held it, no code wrote it).
  prontoMailboxLink: 'pmx', // `pronto_mailbox_links.mailboxLinkId` — one person's link to one email account
  // CROSS-APP-EVENTS-001 U2 (2026-10-19; reserved by the ecosystem coordinator) — the ONE app-to-app event channel,
  // `@puffin-core/app-events`. An event is recorded once and owes one delivery row per consumer app in the producer's
  // `<producer>_app_events_outbox`; a consumer records each event it applied once, in its `<consumer>_app_events_inbox`.
  // `ape`, `aed` and `aei` were measured free (no key held them, no code wrote them).
  appEvent: 'ape', // the event's `eventId` — one recorded fact, shared by its deliveries and the consumer's inbox row
  appEventDelivery: 'aed', // `<producer>_app_events_outbox.appEventDeliveryId` — one event owed to one consumer app
  appEventInbox: 'aei', // `<consumer>_app_events_inbox.appEventInboxId` — one event a consumer has applied
  // MAILBOX-SIGNUP-JOURNEY-001 B8 (2026-10-23) — a Form 1583 identity document recorded for one mail name (the photo
  // ID of box 8 or the address ID of box 9), its number sealed under the dedicated `mailbox-id-document-encryption-key`.
  // `mid` was measured free (no key held it, no code wrote it); it sits beside ParcelPuffin's physical-mailbox family
  // (`mbx`, `mbr`, `mli`, `mmn`, `mh*`).
  mailboxIdDocument: 'mid', // pos-api `cmp_*.mailbox_mail_name_id_documents.idDocumentId`
  // MAILBOX-SIGNUP-JOURNEY-001 B3 / D1 (2026-10-26) — a mailbox rental's agreement records: a store's versioned terms,
  // one agreement per rental term, and its ESIGN signature record (each guarded in the database: terms frozen once
  // published, an agreement's status only forward, a signature append-only). All three were measured free (THINKPAD,
  // at `84ab1d7c03`).
  mailboxAgreementTemplate: 'mct', // pos-api `cmp_*.mailbox_agreement_templates.templateVersionId`
  mailboxAgreement: 'mbc', // pos-api `cmp_*.mailbox_agreements.agreementId`
  mailboxAgreementSignature: 'sig', // pos-api `cmp_*.mailbox_agreement_signatures.signatureId`
  // COMPLIANCE-REPORTING-001 part 2 C2 (2026-10-31; owner ruling R-REFUND-CHECK, "Owed until check written") — a refund
  // paid back by check, owed until the check is written (guarded in the database: owed → paid only, what was written
  // frozen once paid, never deleted). `rcp` was measured free (no key held it, no code wrote it).
  refundCheckPayout: 'rcp', // pos-api `cmp_*.refund_check_payouts.refundCheckPayoutId`
};

/**
 * Generate a type-prefixed entity ID.
 *
 * @param {string} entityType - The entity type key from ID_PREFIXES
 * @returns {string} Prefixed ID in format "{prefix}_{uuid}" (40 chars)
 * @throws {Error} If entityType is not registered in ID_PREFIXES
 *
 * @example
 * generateId('location')  // "loc_550e8400-e29b-41d4-a716-446655440000"
 * generateId('user')      // "usr_6ba7b810-9dad-11d1-80b4-00c04fd430c8"
 */
function generateId(entityType) {
  const prefix = ID_PREFIXES[entityType];
  if (!prefix) {
    throw new Error(`Unknown entity type: "${entityType}". Register in ID_PREFIXES.`);
  }
  return `${prefix}_${uuidv4()}`;
}

/**
 * EPI-TYPE-RESTORATION-001 (2026-09-17) — mint a **deterministic** entity id: the same logical key
 * always produces the same id, and the id is a fully conformant EPI-001 value.
 *
 * ## Why this belongs in the registry package
 *
 * It already existed, in `tests/cross-app/helpers/seedPlannerDemoIds.js`, as a raw SHA-256 tail
 * spliced onto a prefix — `loc_dtown_d6004885f4a18c5b3fdd90bc4df09c`. That violates EPI-001 rules 1
 * and 2 (ALWAYS `generateId`, NEVER raw crypto) and produced ids the registry could not recognise:
 * `isValidPrefixedId` refused them and `entityTypeOf` answered `null`, so every validator derived
 * from `getPrefixPattern` refused a legitimately-seeded row.
 *
 * The capability itself is not the problem — it is load-bearing. The committed PlannerPuffin demo
 * seed is idempotent and REVERSIBLE, and both properties rest on a stable id for a given logical
 * key. That seed, applied to `dtownretail` under owner approval, is what exposed six real
 * production defects that every unit test had passed. So the answer was to build it out here, on
 * the standard's own mechanism, rather than to exempt it or to prune it.
 *
 * ## Why v5 and not a forced-`4` digest
 *
 * Version 5 IS the RFC-4122 name-based UUID — deterministic by construction and a real UUID.
 * Formatting a SHA-256 digest with the version nibble forced to `4` would mint a value that claims
 * to be random and is not; anything reading the version would be lied to.
 *
 * @param {string} entityType - Key from `ID_PREFIXES`.
 * @param {...(string|number)} parts - The logical key. Order matters; every part is significant.
 * @returns {string} `<prefix>_<uuid-v5>` — 40 chars, and `isValidPrefixedId` accepts it.
 * @throws {Error} If `entityType` is unregistered or no parts are supplied.
 *
 * @example
 *   deterministicId('location', 'dtown', 'group');   // stable across runs and machines
 *   deterministicId('transaction', 'dtown', 's01', '20260624', 0);
 */
function deterministicId(entityType, ...parts) {
  const prefix = ID_PREFIXES[entityType];
  if (!prefix) {
    throw new Error(
      `deterministicId: unknown entity type "${entityType}". Register it in ID_PREFIXES.`,
    );
  }
  if (parts.length === 0) {
    throw new Error(
      `deterministicId: at least one key part is required for "${entityType}" — an id derived from ` +
        'nothing but its type would be the same for every row of that type.',
    );
  }
  // 🔴 LENGTH-PREFIXED, not separator-joined. The first cut joined on `|` with a comment asserting
  // that ('a','b') and ('a|b') could not collide — and the very next probe showed they produced the
  // SAME id, because a separator is only unambiguous if it cannot appear in a part, and nothing
  // enforced that. Two different logical keys colliding on one id is silent data corruption in an
  // UPSERT-shaped seed. `<len>:<part>` is unambiguous by construction, with no rule for a caller to
  // obey and no comment to be wrong about.
  const key = parts
    .map((part) => {
      const text = String(part);
      return `${text.length}:${text}`;
    })
    .join('');
  return `${prefix}_${uuidv5(key, getPuffinIdNamespace())}`;
}

/**
 * Regex pattern matching a valid prefixed ID: {3-char-prefix}_{uuidv4}
 * Use with express-validator's .matches() or standalone.
 *
 * @example
 * body('userId').matches(PREFIXED_ID_PATTERN).withMessage('Invalid ID format')
 */
/**
 * The EPI-001 body: an RFC-4122 UUID, version **4 (random)** or **5 (name-based, deterministic)**.
 *
 * 🔴 This accepted version 4 ONLY until 2026-09-17, and that was not a considered decision — it was
 * the shape `generateId` happened to emit. The cost surfaced when EPI-TYPE-RESTORATION-001 Q-12
 * derived ~350 route validators from `getPrefixPattern` instead of hand-typing them: the committed
 * PlannerPuffin demo seed (RPT-CANON P11, applied to `dtownretail` under owner approval, and the
 * vehicle that exposed six real production defects because "the platform's bugs all pass unit
 * tests") mints ids that ARE 40 characters and DO carry a registered prefix — and every one of them
 * was refused, by `isValidPrefixedId`, by `entityTypeOf`, and now by every derived validator.
 *
 * The seed needs determinism: it is idempotent (re-running UPSERTs the same rows) and reversible
 * (`--down` removes exactly what it wrote), and both properties rest on the same logical key always
 * producing the same id. A random v4 cannot do that. So the seed had been minting a raw SHA-256
 * tail, which violates EPI-001 rules 1 and 2 (always `generateId`, never raw crypto) and is why its
 * ids were unrecognisable to the registry.
 *
 * **Version 5 is the standard's own answer to that requirement** — a name-based UUID, deterministic
 * by construction, and a real RFC-4122 UUID rather than a lookalike. Admitting it is therefore a
 * widening of the CANON to cover a legitimate minter, not a concession to a non-conforming one; the
 * alternative, forcing the version nibble to `4` on a value that is not random, would have minted
 * an id that lies about how it was made.
 *
 * Nothing else is admitted. A `tgt_` id still fails (unregistered prefix), `loc_abc` still fails
 * (width), and the old raw-digest seed tail still fails (no dashes, no version nibble) — which is
 * exactly why the seed had to move onto `deterministicId` below rather than be exempted.
 */
const UUID_BODY = '[0-9a-f]{8}-[0-9a-f]{4}-[45][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}';

/**
 * The ParcelPuffin ecosystem's UUID-v5 namespace. Fixed forever: change it and every deterministic id
 * ever minted changes with it, which would orphan every seeded row instead of re-deriving it.
 *
 * A v5 namespace is itself just a UUID; this one is the RFC-4122 DNS namespace re-rooted under a
 * name specific to this ecosystem (`puffin-ecosystem.epi-001`), so it cannot collide with another product's derivations.
 */
let puffinIdNamespace = null;
function getPuffinIdNamespace() {
  if (puffinIdNamespace === null) {
    puffinIdNamespace = uuidv5('puffin-ecosystem.epi-001', '6ba7b810-9dad-11d1-80b4-00c04fd430c8');
  }
  return puffinIdNamespace;
}

const PREFIXED_ID_PATTERN = new RegExp(`^[a-z]{3}_${UUID_BODY}$`);

/**
 * Validate that a string is a valid prefixed entity ID.
 *
 * @param {string} id - Value to validate
 * @returns {boolean} True if valid prefixed ID
 */
function isValidPrefixedId(id) {
  return typeof id === 'string' && PREFIXED_ID_PATTERN.test(id);
}

/**
 * T1-13 (2026-05-14) — return a regex pattern that matches ONLY a prefixed
 * ID whose 3-char prefix corresponds to the given `entityType`.
 *
 * Pre-T1-13, validators across the ecosystem hand-rolled patterns like
 * `/^usr_[0-9a-f]{8}-[0-9a-f]{4}-4.../` per entity type — 18+ inline
 * regexes in port-api alone, plus sibling-app duplicates. Each
 * hand-roll was a tiny opportunity for the prefix to drift away from
 * the registry SSOT.
 *
 * The returned regex is anchored (`^…$`) so it works as a standalone
 * predicate AND as the pattern in `Joi.string().pattern(...)`.
 *
 * @param {string} entityType - Key from ID_PREFIXES.
 * @returns {RegExp} A `RegExp` matching exactly the entity-type's prefix.
 * @throws {Error} If entityType is neither registered in ID_PREFIXES nor a RETIRED_ENTITY_PATTERNS row.
 *
 * @example
 *   const pattern = getPrefixPattern('user');
 *   pattern.test('usr_550e8400-e29b-41d4-a716-446655440000'); // true
 *   pattern.test('loc_550e8400-e29b-41d4-a716-446655440000'); // false
 */
/**
 * TENANT-ID-001 — the tenant-id shapes, owned HERE because a tenant id is an EPI-001 *exception*
 * and this package is where EPI-001 id shape lives (EPI-TYPE-RESTORATION-001 Q-12, 2026-09-17).
 *
 * ## Why they moved out of `@puffin-core/database`
 *
 * Because half the fleet could not reach them there. `@puffin-core/database` carries knex and pg;
 * `feature-flags`, `changelog`, `passkey`, `test-isolation`, `model-base` and `unified-models` all
 * need to know what a tenant id looks like and none of them may take a database driver as a
 * dependency to find out. So each one re-typed the fact, and the copies disagree:
 *
 * ```
 *   packages/model-base        COMPANY_ID_REGEX        /^cmp_[a-zA-Z0-9]+$/     uppercase, any length
 *   packages/feature-flags     TENANT_ID_PATTERN       /^cmp_[a-zA-Z0-9]+$/     same
 *   packages/unified-models    COMBINED_TENANT_PATTERN /^cmp_[a-z0-9]{12}$/     🔴 misNAMED
 *   packages/middleware        TENANT_ID_PATTERNS.ANY  /^cmp_[a-z0-9]{12}$/     🔴 identical to .CMP
 *   packages/tenant-provisioning TENANT_ID_PATTERNS.cmp /^cmp_[a-z0-9]{12}$/    🔴 looser on the WRITE path
 *   packages/changelog         COMPANY_ID_PATTERN      /^cmp_[a-f0-9]{12}$/     refuses test tenants
 * ```
 *
 * Two of those NAMED a union they did not deliver, and the two needed OPPOSITE fixes — which is the
 * part worth remembering, because they look identical in the table:
 *
 *   - `TENANT_ID_PATTERNS.ANY` was a BEHAVIOUR defect. It is a resolution-layer guard, its name and
 *     docblock both promised a union, and refusing every disposable schema was simply wrong. It is
 *     now `ANY_TENANT_ID_PATTERN`. `tenant-provisioning`'s copy is the same class and worse placed:
 *     being LOOSER than canonical on the PROVISION path let `provision()` mint a schema that
 *     `forEachTenantSchema` could never address again — a tenant you can make and cannot query. It
 *     defers to the canonical union now too.
 *   - `COMBINED_TENANT_PATTERN` was a NAMING defect only. Its strictness is a standing ruling
 *     (CENSUS-GRID 2026-08-14 — a production validation surface never widens to admit test
 *     artifacts), enforced by a negative control that asserts the real models keep refusing a
 *     disposable name. It is now `PRODUCTION_TENANT_ID_PATTERN`, with its accepted language
 *     untouched. EPI-TYPE-RESTORATION-001 read the table, inferred "same defect, same fix", widened
 *     it — and the negative control caught it that same hour.
 *
 * So: a name that lies is worth fixing on sight, but the fix is whichever of the name and the
 * behaviour is actually wrong, and a census table cannot tell you which. That is exactly how `tgt_`
 * survived: a fact copied until the copies stopped agreeing, with nothing able to notice.
 *
 * This package has ZERO `@puffin-core` dependencies and is dual-use (it has a `browser` export), so
 * it is the only place every tier can reach. `@puffin-core/database` re-exports these, so
 * `TenantValidator` and its existing consumers are unchanged.
 */

/**
 * The TENANT-ID-001 prefix, as one string. Everything below composes from it rather than repeating
 * it, so `cmp_` appears exactly once in this package.
 *
 * @type {string}
 */
const TENANT_PREFIX = 'cmp';

/** A real (production) tenant: prefix + 12 lowercase hex. Doubles as the PostgreSQL schema name. */
const TENANT_ID_PATTERN = /^cmp_[a-f0-9]{12}$/;

/**
 * A TII-001E disposable/test tenant schema: `cmp_test<12hex>`, cloned from `cmp_template` by
 * `@puffin-core/test-isolation`. A REAL schema in the shared dev==prod database.
 */
const TEST_TENANT_ID_PATTERN = /^cmp_test[a-f0-9]{12}$/;

/**
 * The ONE designated persistent test tenant (TEST-TENANT-CONSOLIDATION, 2026-08-14): real-shaped (`TENANT_ID_PATTERN`),
 * at full template parity, reached by every fleet migration. Its id here, not only in `@puffin-core/test-isolation`,
 * because production code names it too since TENANT-ID-ORPHAN-REPAIR-001: its `public.companies` record carries the
 * test role `'readTarget'` (the read-only fixture no audience but resolution reaches), written by a pos-api migration. `test-isolation`'s
 * `DESIGNATED_TEST_TENANT_SCHEMA` re-exports this value.
 */
const DESIGNATED_TEST_TENANT_ID = 'cmp_000000000001';

/**
 * The ONE designated test COMPANY: the company the browser e2e suites log in to (subdomain `test-cmp-a1b2c3d4e5f6`), a
 * live, written-to tenant with a real `public.companies` record. Not to be confused with the designated test TENANT
 * above. Its id here because production code names it since TENANT-ID-ORPHAN-REPAIR-001: a pos-api migration gives
 * its company record the test role `'canary'`, which maintenance sweeps run FIRST. `@puffin-core/test-helpers`'
 * `E2E_TEST_COMPANY` reads this value.
 */
const DESIGNATED_TEST_COMPANY_ID = 'cmp_a1b2c3d4e5f6';

/**
 * The ONE pattern of a LEAKED TEST-FIXTURE partition value (TENANT-ID-ORPHAN-REPAIR-001).
 *
 * Its domain is a PARTITION KEY, a tenant-id value stored in a row of a shared table. A prefix family: the
 * disposable-schema families (`cmp_test…`), the chain-proof suites' `cmp_chainprooftest…` and the GC probe's
 * `cmp_gctest`, which key test chain partitions and never exist as schemas. A real tenant id is `cmp_<12 hex>` and can
 * never match (`t` and `g` are not hex digits), nor can `cmp_000000000001`, `cmp_template` or a NULL partition.
 * A SCHEMA name is judged by {@link DISPOSABLE_SCHEMA_PATTERN}, never by this family.
 *
 * A STRING, not a RegExp, because it is read in TWO languages: JavaScript (`new RegExp(…)`) and PostgreSQL (`~`) — pos
 * migration `20261005142980` inlines it into the audit-chain partition guard, which admits emptying a partition only
 * when its value matches. Declared ONCE here (production code reads it, so its home cannot be a dev-only package);
 * `@puffin-core/test-isolation`'s leaked-partition sweep reads the same value.
 */
const LEAKED_PARTITION_PATTERN = '^cmp_(test|chainprooftest|gctest)';

/**
 * The ONE pattern of a key a TEST minted through `@puffin-core/test-isolation`'s `resourceId` family:
 * `<3-char prefix>_<idPrefix>_<tail>`, where the run's `idPrefix` is `t` + 16 base-36 characters.
 *
 * Its domain is a KEY of a shared PLATFORM CATALOG row (a slug or an id, not a tenant partition): the leaked-row sweep
 * reads it for the catalog tables a suite writes into (`public.voice_features`, `public.voice_templates`), and the
 * per-suite cleanup refuses any key it does not match. A `generateId` value can never match (its body is a UUID, and
 * `t` is not a hex digit), nor can a seeded catalog key (`timecard`, `posTransactions`): a match is a test's row.
 *
 * A STRING, like {@link LEAKED_PARTITION_PATTERN}, so JavaScript (`new RegExp(…)`) and PostgreSQL (`~`) read the same
 * text.
 */
const TEST_ISOLATION_KEY_PATTERN = '^[a-z]{3}_t[0-9a-z]{16}_';

/**
 * The ONE rule of a THROWAWAY CLONE SCHEMA's name, as a regular-expression source, for a tenant prefix (TENANT-ID-
 * ORPHAN-FOLLOWON-001; coordinator ruling 2026-10-06: one home, each domain stated).
 *
 * Its domain is a SCHEMA name: every name `@puffin-core/test-isolation`'s disposable clone and `@puffin-core/test-helpers`'
 * shadow schema have ever minted. That is `<prefix>test<12 hex>` today ({@link TEST_TENANT_ID_PATTERN}) and
 * `<prefix>test_<12 hex>` before Q-N2, which a sweep must still reclaim. Anchored at both ends, because it selects
 * schemas for `DROP SCHEMA … CASCADE`: exact, never a family. A live tenant `<prefix><12 hex>` cannot match, because `t` is
 * not a hex digit. Every name it admits is also a {@link LEAKED_PARTITION_PATTERN} value (a clone's rows key by its
 * schema).
 *
 * @param {string} [prefix='cmp_'] - The tenant prefix, ending in `_` (test-isolation's configurable `tenantPrefix`);
 *   regex-escaped here.
 * @returns {string} e.g. `^cmp_test_?[a-f0-9]{12}$`.
 * @throws {TypeError} When the prefix is not a string ending in `_`.
 */
function disposableSchemaPatternSource(prefix = `${TENANT_PREFIX}_`) {
  if (typeof prefix !== 'string' || !prefix.endsWith('_')) {
    throw new TypeError(
      `disposableSchemaPatternSource: prefix must be a string ending in "_" (got ${String(prefix)})`,
    );
  }
  return `^${prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}test_?[a-f0-9]{12}$`;
}

/**
 * The canonical throwaway clone schema's name rule (`cmp_` prefix). A STRING, like {@link LEAKED_PARTITION_PATTERN},
 * because PostgreSQL reads it too: pos migration `20261005142995` inlines it into the unsealed-insert guard's one-chain
 * arm, which judges a table's SCHEMA.
 */
const DISPOSABLE_SCHEMA_PATTERN = disposableSchemaPatternSource();

/**
 * The ONE name of the schema every tenant is cloned from (TEMPLATE-HYGIENE-001, owner ruling Q-P5-2). It is a STRUCTURE
 * source: `clone_schema` copies its tables and zero rows, and a company's starting data is built from code, never read
 * from it. It is not a tenant ({@link ANY_TENANT_ID_PATTERN} refuses it), though the identifier-safety pattern admits it.
 */
const TENANT_TEMPLATE_SCHEMA = 'cmp_template';

/**
 * Is `schema` the tenant template?
 *
 * @param {*} schema
 * @returns {boolean} true for {@link TENANT_TEMPLATE_SCHEMA} exactly
 */
function isTenantTemplateSchema(schema) {
  return schema === TENANT_TEMPLATE_SCHEMA;
}

/**
 * The ONE refusal a runtime seam that writes a tenant's rows applies to the template: a row there is residue every
 * later clone is judged against (the 2026-09 residue — 5,390 rows across 22 tables — came from suites that used the
 * template as their tenant).
 *
 * @param {string} fnName - the refusing seam, for the message
 * @param {string} schema
 * @returns {string} `schema`, when it is not the template
 * @throws {Error} `TENANT_TEMPLATE_WRITE_REFUSED`, naming the seam
 */
function assertNotTenantTemplateSchema(fnName, schema) {
  if (isTenantTemplateSchema(schema)) {
    throw Object.assign(
      new Error(
        `${fnName}: ${TENANT_TEMPLATE_SCHEMA} is the tenant template — a structure source, never a tenant; no tenant ` +
          'row is written there',
      ),
      { code: 'TENANT_TEMPLATE_WRITE_REFUSED' },
    );
  }
  return schema;
}

/**
 * Tenant-SHAPED schemas that are not companies — the ONE declaration every coverage, repair and contract arm reads, so
 * none of them matches the exception by a pattern of its own.
 *
 * - `cmp_7666384ea613` — the source-migration rehearsal sandbox: no company record, no Owner. Owner ruling 2026-10-08
 *   ~16:30 PDT: "Leave it as a sandbox". No starting-data repair reaches it; no coverage arm judges it.
 *
 * Its END: the ruling removes this tenant at the ThermoGrid cutover (R-173), so the entry is DELETED in the same change
 * that drops the schema. A runtime contract fails while a listed schema no longer exists, so the list cannot outlive
 * the tenant it names.
 *
 * @constant {ReadonlyArray<string>}
 */
const NON_COMPANY_TENANT_SCHEMAS = Object.freeze(['cmp_7666384ea613']);

/**
 * Is `schema` a tenant-shaped schema that is not a company ({@link NON_COMPANY_TENANT_SCHEMAS})?
 *
 * @param {*} schema
 * @returns {boolean}
 */
function isNonCompanyTenantSchema(schema) {
  return NON_COMPANY_TENANT_SCHEMAS.includes(schema);
}

/** The two meanings a tenant-id column can carry (TENANT-ID-ORPHAN-REPAIR-001). */
const TENANT_ID_SEMANTICS = Object.freeze({
  /** The value must equal the schema it lives in — the schema IS the tenant. */
  OWN_SCHEMA: 'own-schema',
  /** The value names some company, by design another one (a migration's target tenant). */
  ANY_COMPANY: 'any-company',
});

/**
 * Every tenant-id column NAME and what it must hold (TENANT-ID-ORPHAN-REPAIR-001). Declared ONCE, here, because both
 * `@puffin-core/database` (the fleet census of tenant-id references) and `@puffin-core/model-base` (the write-side
 * refusal) judge by it, and id-generator is the one package both depend on. A new cross-tenant column is a reviewed
 * entry here, never a silent exemption (live read L2: `prospect_migration_runs.targetTenantId` names its target company
 * by design).
 *
 * @type {Readonly<Object<string, string>>}
 */
const TENANT_ID_COLUMNS = Object.freeze({
  companyId: TENANT_ID_SEMANTICS.OWN_SCHEMA,
  tenantId: TENANT_ID_SEMANTICS.OWN_SCHEMA,
  targetTenantId: TENANT_ID_SEMANTICS.ANY_COMPANY,
});

/**
 * The own-schema tenant columns of a record that name a tenant OTHER than the schema it is written into. `null` and
 * `undefined` hold no tenant and are never a mismatch; any other value that is not the schema is.
 *
 * @param {Object} record - A row about to be written.
 * @param {string} schema - The tenant schema it is written into.
 * @returns {Array<{column: string, value: *}>} In vocabulary order; empty when the record is consistent.
 */
function ownTenantColumnMismatches(record, schema) {
  if (!record || typeof record !== 'object') return [];
  const mismatches = [];
  for (const [column, semantics] of Object.entries(TENANT_ID_COLUMNS)) {
    if (semantics !== TENANT_ID_SEMANTICS.OWN_SCHEMA) continue;
    const value = record[column];
    if (value !== null && value !== undefined && value !== schema) {
      mismatches.push({ column, value });
    }
  }
  return mismatches;
}

/**
 * 🔴 **The one a boundary should use.** Either legitimate shape, in a single RegExp — for the
 * callers that need a pattern rather than a predicate, a Joi `.pattern(…)` above all.
 *
 * Disjoint by construction, which is what makes the union safe rather than merely convenient: a
 * real id is exactly 12 characters after the prefix and a disposable id is 16 (`test` + 12), so
 * widening admits no string that was not already a legitimate tenant schema. It refuses
 * `cmp_template` (not a tenant), uppercase, unbounded length, and anything carrying SQL.
 */
const ANY_TENANT_ID_PATTERN = /^cmp_(?:test)?[a-f0-9]{12}$/;

/**
 * The two classes of tenant-id SURFACE — every CHECK and write-path refusal on a tenant id states which it is, and takes
 * its pattern from {@link tenantIdPatternFor}, so the class decides the pattern and no surface picks one ad hoc
 * (TENANT-ID-ORPHAN-FOLLOWON-001; coordinator rulings 2026-10-05, recorded in log/rulings.md, under the owner's
 * CENSUS-GRID ruling "a production validation surface never widens to admit test artifacts"):
 *
 * - `directory` — a row there makes a tenant RESOLVABLE in production (`public.companies`). Real tenants
 *   only: {@link TENANT_ID_PATTERN}. A suite that must write one uses a
 *   disposable COPY of the table, never the production one.
 * - `test-aware` — a log, chain, counter or routing surface that suites must exercise END-TO-END through the production
 *   path. Real and test tenants: {@link ANY_TENANT_ID_PATTERN}. A surface earns this class only when its test rows are
 *   provably unreachable in production, torn down by the suite that wrote them, and swept if that suite is killed —
 *   the cross-app `tenantSurfaceClass` contract checks all three against the declared registry.
 *
 * @type {Readonly<{DIRECTORY: 'directory', TEST_AWARE: 'test-aware'}>}
 */
const TENANT_SURFACE_CLASSES = Object.freeze({ DIRECTORY: 'directory', TEST_AWARE: 'test-aware' });

const TENANT_ID_PATTERN_BY_SURFACE_CLASS = Object.freeze({
  [TENANT_SURFACE_CLASSES.DIRECTORY]: TENANT_ID_PATTERN,
  [TENANT_SURFACE_CLASSES.TEST_AWARE]: ANY_TENANT_ID_PATTERN,
});

/**
 * The tenant-id pattern a surface of this class enforces.
 *
 * @param {'directory'|'test-aware'} surfaceClass - One of {@link TENANT_SURFACE_CLASSES}.
 * @returns {RegExp}
 * @throws {TypeError} For anything else — a surface must choose a declared class.
 */
function tenantIdPatternFor(surfaceClass) {
  if (
    typeof surfaceClass !== 'string' ||
    !Object.hasOwn(TENANT_ID_PATTERN_BY_SURFACE_CLASS, surfaceClass)
  ) {
    throw new TypeError(
      `tenantIdPatternFor: surface class must be one of ${Object.values(TENANT_SURFACE_CLASSES).join(', ')} — got ` +
        `${JSON.stringify(surfaceClass)}`,
    );
  }
  return TENANT_ID_PATTERN_BY_SURFACE_CLASS[surfaceClass];
}

/**
 * 🔴 The IDENTIFIER-SAFETY pattern — **not** a tenant-identity check. Read the difference before
 * using either; conflating them is a mistake that costs a fleet-wide fixture rewrite.
 *
 * A tenant id doubles as the PostgreSQL schema name, so it is interpolated directly into SQL by
 * `BaseModel.getQualifiedTableName` (`${companyId}.${tableName}`) on **every** tenant-scoped query.
 * What that interpolation needs to guarantee is that the value cannot carry SQL — no quote, no
 * semicolon, no whitespace, no comment marker. That is this pattern's whole job, and it is the one
 * `validateCompanyId` enforces.
 *
 * It deliberately does NOT answer "is this a real tenant?". That question belongs where tenants are
 * RESOLVED — `TenantValidator.isValidTenantId`, the auth middleware's `isValidTenantId`,
 * `tenantUtils.validateTenantId` — all of which use `ANY_TENANT_ID_PATTERN` above and have always
 * refused a non-canonical value. A schema name that is safe but does not exist fails loudly at
 * query time, which is the correct outcome for a value that got past resolution.
 *
 * ## Why these are two patterns and not one, when `tenantUtils` being looser WAS a defect
 *
 * The distinction is the QUESTION, not the strictness. `tenantUtils.validateTenantId` claimed to be
 * the tenant-identity validator and was looser than the canonical one — the same question with two
 * answers, and the looser one on the write path, which let `provision()` create a schema that
 * `forEachTenantSchema` could never address again. That is a real defect and it is fixed.
 *
 * This is a different question asked at a different layer. Raising it to the canonical shape would
 * not make anything safer — the interpolation is already safe — and it would demand that ~77,000
 * opaque test doubles become UUIDs to satisfy a guard that never needed them. A guard whose correct
 * enforcement requires rewriting half the repository's test fixtures is aimed at the wrong layer.
 *
 * Derived, not hand-typed: this constant is what `model-base` imports, so Q-12's rule is satisfied
 * and there is no regex for a future author to retype slightly differently.
 */
const TENANT_SCHEMA_IDENTIFIER_PATTERN = /^cmp_[A-Za-z0-9]+$/;

/**
 * The body bound `getIdPrefixPattern` applies (EPI-TYPE-RESTORATION-001 Q-19, 2026-09-19).
 *
 * 36 is arithmetic, not taste: an EPI-001 column is `VARCHAR(40)`, less three for the prefix and
 * one for the underscore. So the bound cannot refuse a value a column can hold, and the fleet scan
 * that proved it newly rejected ZERO of the values the unbounded pattern accepted under a
 * registered prefix (34,391 columns, nine schemas; longest accepted value exactly 40).
 *
 * Named once here rather than inlined, so the next reader changes one thing and so the arithmetic
 * has somewhere to be written down.
 *
 * @type {string}
 */
const ID_BODY_BOUND = '[A-Za-z0-9_-]{1,36}';

/**
 * EPI-TYPE-RESTORATION-001 — the TYPE check: `^<prefix>_`, derived from the registry.
 *
 * ## Why this exists beside `getPrefixPattern`
 *
 * They answer different questions, and conflating them refuses real rows.
 *
 * - `getIdPrefixPattern(type)` — **"is this a `<type>` id?"** The question a boundary actually asks.
 *   It is what catches a customer id passed where a department belongs, which is the mixup EPI-001's
 *   rationale names and the one that started this initiative.
 * - `getPrefixPattern(type)` — **"is this a well-formed `<type>` id?"** Prefix AND body. Correct
 *   where the value must have come from a minter in this process — a freshly generated id, a test
 *   asserting what `generateId` produced.
 *
 * 🔴 Reaching for the second where the first belongs is how a validator starts refusing production
 * data. Measured 2026-09-17, the live database holds ids with a correct registered prefix and a
 * non-canonical BODY: `pch_t2f129a1d_a1i` (TII-001 isolation residue in D Town Retail) and
 * `loc_…_pm_seed` (a dormant PostalMate rehearsal). (Those two particular rows have since been
 * removed — pos Batches 400 / 406 — and `usr_postalmate_import` + `loc_43d53395_pm_seed` carry the
 * shape today; the SHAPE is the point, not the rows.) A full-shape gate rejects every one of them —
 * which is precisely the `tgt_` failure this initiative exists to remove, pointed the other way.
 *
 * Deriving the prefix is what Q-12 requires; deriving the BODY was never asked for and changes what
 * production accepts.
 *
 * ## The body is BOUNDED, and the bound was proven before it landed (Q-19, 2026-09-19)
 *
 * This used to return `^<prefix>_` with no end anchor, so `usr_` followed by ten kilobytes of
 * anything satisfied it. The owner's ruling: *"a boundary validator that accepts a 10 KB string is
 * not enterprise-grade. Bound the body, proven against the live data before it lands."*
 *
 * `{1,36}` is not a taste: an EPI-001 column is `VARCHAR(40)`, and 40 − 3 for the prefix − 1 for
 * the underscore leaves 36, so the bound **cannot refuse anything a column is physically able to
 * store**. `{1,…}` rather than `{0,…}` because `usr_` alone names nothing.
 *
 * 🔴 **Measured across the fleet before landing, not reasoned about**: 34,391 columns in all nine
 * `cmp_%` schemas, every value the OLD pattern accepted under a registered prefix. Newly rejected:
 * **zero**. Longest accepted value: exactly 40 characters. The scan did surface six columns whose
 * values match `^[a-z]{3}_` and would fail the bound — every one an EMAIL (`ben_sarp@yahoo.com`,
 * `ajs_65@icloud.com`), none under a registered prefix, so no derived pattern ever judges them.
 * That distinction is why the scan filters by the registry: the first version of it did not, and
 * would have reported a confident refutation of its own query shape.
 *
 * The character class is deliberately permissive WITHIN the width — the live database holds
 * `usr_postalmate_import` and `loc_43d53395_pm_seed` (re-measured 2026-09-24; the earlier examples
 * `loc_bb4b6ad9_pm_seed` and `pch_t2f129a1d_a1i` were test residue, since removed by pos Batches
 * 406 and 400). What it
 * refuses is whitespace, quotes, semicolons and slashes: shapes that are not ids in any generation
 * this codebase has had, and that only arrive at a boundary as an injection or a bug.
 *
 * @param {string} entityType - Key from `ID_PREFIXES`.
 * @returns {RegExp} `^<prefix>_[A-Za-z0-9_-]{1,36}$` — the type, and a body no wider than the column.
 * @throws {Error} If `entityType` is not registered.
 *
 * @example
 *   getIdPrefixPattern('location');   // /^loc_[A-Za-z0-9_-]{1,36}$/
 */
function getIdPrefixPattern(entityType) {
  const prefix = ID_PREFIXES[entityType];
  if (!prefix) {
    throw new Error(
      `getIdPrefixPattern: unknown entity type "${entityType}". Register in ID_PREFIXES.`,
    );
  }
  return new RegExp(`^${prefix}_${ID_BODY_BOUND}$`);
}

/**
 * Compose an id pattern from a REGISTERED prefix and a caller-supplied BODY.
 *
 * EPI-TYPE-RESTORATION-001 (2026-09-17). The two patterns above are the endpoints of a spectrum and
 * a large part of this codebase sits between them: a validator written as
 * `/^pid_[0-9a-f-]{36}$/` is making TWO independent assertions — "this is a phoneIdentity id"
 * (the prefix) and "its body looks like this" (the author's domain knowledge). Only the first is
 * this initiative's defect. Forcing such a site onto `getPrefixPattern` TIGHTENS it and forcing it
 * onto `getIdPrefixPattern` LOOSENS it; neither is a faithful derivation, and both were measured
 * doing real damage:
 *
 * - Tightening rejects production data. The live database holds **2,449,841** entity-id values with
 *   a correct registered prefix and a non-canonical body — 1,842,889 of them
 *   `transactions.userId = 'usr_postalmate_import'` in the real production tenant, plus sentinels
 *   like `'system'`. (The prefix-LESS department residual `'__unassigned__'` was measured beside
 *   them; PLN-DEPT-PLAN-001 round 6 / Q-20 retired it for a real, system-seeded `dep_` department,
 *   so it is no longer an example of anything a validator must admit.)
 * - Loosening removes a guard that was doing work. A route `:id` param guard written with a body
 *   check refuses `pid_not-a-uuid` BEFORE the model is consulted; drop the body and the malformed
 *   id reaches the data layer.
 *
 * The two facts are reconcilable because the original authors were right about their own domains:
 * the types whose live ids carry sentinels (`usr_`, `loc_`, `pch_`, the payday `*_uat_*` family)
 * are exactly the ones whose validators never checked a body, and the types with a body check
 * (`pid_`, `cqu_`, `erp_`, …) have no non-canonical value in the database. So the faithful move is
 * to derive the PREFIX and keep whatever body the site already asserted.
 *
 * 🔴 NOT AT A REQUEST BOUNDARY. What a boundary accepts was ruled separately (GAMING, coord `log/rulings.md`
 * :4884–4885, the bullet and its annotation quoted together, verbatim):
 *
 *     **Ruling: RVC's by-kind rule governs at every REQUEST BOUNDARY** (`joiPrefixedId` / `getIdPrefixPattern`).
 *     EPI #63 stays the rule for what an id IS (generation and storage), not for what a boundary accepts. A
 *     live-data census can't decide a boundary rule: it flips on the first non-uuid id and re-creates the
 *     422-then-400 class wherever a declaration and its guard disagree.
 *
 *     **ANNOTATION (6:02 AM PDT, 2026-10-25, GAMING; the original is left as written):** the parenthetical was
 *     WRONG about `joiPrefixedId`. Measured in `packages/id-generator/lib/index.js`: `joiPrefixedId` (:2944-2960)
 *     builds on `getPrefixPattern` (:2817-2825), which is `^<prefix>_<UUID_BODY>$`, a uuid body, NOT by kind.
 *     **What this ruling means:** at a request boundary an id is judged by `getIdPrefixPattern(entityType)`
 *     (:2723-2731; the registered prefix plus the bounded body `ID_BODY_BOUND`). That pattern REPLACES
 *     `joiPrefixedId`, `getPrefixPattern` and uuid-bodied `buildIdPattern` at a boundary. RVC's boundary guard
 *     reporting `joiPrefixedId` there, and RVC F1's conversion of prospect-api's 39 `joiPrefixedId` calls to
 *     `getIdPrefixPattern`, are both CONSISTENT with this ruling. Quote this annotation together with the bullet
 *     above it. (Raised by RVC via THINKPAD 1300Z.)
 *
 * So the body argument above holds for what an id IS (generation, storage, a model's own guard on its own rows), and a
 * request boundary judges an id by `getIdPrefixPattern(entityType)` alone. `api/no-id-body-pattern-at-boundary`
 * (REQUEST-VALIDATION-CONVERGE-001 unit 9) reports this function in a boundary module.
 *
 * @param {string} entityType - Key from `ID_PREFIXES`.
 * @param {string} bodyPattern - Regex SOURCE for the body, without anchors (e.g. `'[0-9a-f-]{36}'`).
 * @returns {RegExp} `^<prefix>_<bodyPattern>$`
 * @throws {Error} If `entityType` is not registered, or `bodyPattern` is not a non-empty string.
 *
 * @example
 *   buildIdPattern('phoneIdentity', '[0-9a-f-]{36}');   // /^pid_[0-9a-f-]{36}$/
 */
/**
 * `buildIdPattern`'s tenant counterpart: derive the TENANT-ID-001 prefix, keep the caller's body.
 *
 * EPI-TYPE-RESTORATION-001 (2026-09-17). The four tenant constants above cover the shapes the
 * ecosystem MINTS, and a call site whose rule is narrower or wider than all four has nowhere to go:
 * `apps/purchase-api/src/database/tenantDb.js` validates `cmp_` + exactly twelve LOWERCASE
 * ALPHANUMERICS, which is neither `TENANT_ID_PATTERN` (hex only) nor
 * `TENANT_SCHEMA_IDENTIFIER_PATTERN` (any case, any length). Five negative controls in its suite pin
 * the difference, so substituting either one measurably drops a guard.
 *
 * Tightening it instead is not available: measured 2026-09-17, the repo holds **18,211** `cmp_`
 * literals across **3,318** files that a hex-only pattern rejects (`cmp_test123`, `cmp_abc`,
 * `cmp_1`). Tenant-id conformance is its own initiative; see Q-21.
 *
 * So the same resolution applies as for entity ids — derive the part that was the defect (the
 * hand-typed prefix) and leave the part that was never the defect (the body) alone.
 *
 * @param {string} bodyPattern - Regex SOURCE for the body, without anchors (e.g. `'[a-z0-9]{12}'`).
 * @param {string} [flags] - Regex flags to preserve.
 * @returns {RegExp} `^cmp_<bodyPattern>$`
 * @throws {Error} If `bodyPattern` is not a non-empty string.
 *
 * @example
 *   buildTenantPattern('[a-z0-9]{12}');   // /^cmp_[a-z0-9]{12}$/
 */
function buildTenantPattern(bodyPattern, flags = '') {
  if (typeof bodyPattern !== 'string' || bodyPattern.length === 0) {
    throw new Error(
      `buildTenantPattern: bodyPattern must be a non-empty regex source string, got ${typeof bodyPattern}.`,
    );
  }
  return new RegExp(`^${TENANT_PREFIX}_${bodyPattern}$`, flags);
}

function buildIdPattern(entityType, bodyPattern, flags = '') {
  const prefix = ID_PREFIXES[entityType];
  if (!prefix) {
    throw new Error(
      `buildIdPattern: unknown entity type "${entityType}". Register in ID_PREFIXES.`,
    );
  }
  if (typeof bodyPattern !== 'string' || bodyPattern.length === 0) {
    throw new Error(
      `buildIdPattern: bodyPattern must be a non-empty regex source string, got ${typeof bodyPattern}.`,
    );
  }
  return new RegExp(`^${prefix}_${bodyPattern}$`, flags);
}

/**
 * Entity types RETIRED from `ID_PREFIXES` whose PATTERN an applied migration still reads. An applied migration is never
 * edited (`GUIDES/shared/standards/database/migrations.md`), so retiring a type that frozen history judges ids by would
 * make that file throw on load, and with it every `migrate:list`. This table is CLOSED and named: each row answers
 * {@link getPrefixPattern} only, with the identical regex the run used, and names its reason and the applied file it
 * serves. `generateId`, `buildIdPattern`, `joiPrefixedId`, `ENTITY_TYPE_BY_PREFIX` and the lint vocabulary never see
 * it, so no new id is minted, validated or recognised under a retired type.
 *
 * @type {Readonly<Record<string, Readonly<{prefix: string, reason: string, appliedFile: string}>>>}
 */
const RETIRED_ENTITY_PATTERNS = Object.freeze({
  campaign: Object.freeze({
    prefix: 'cmp',
    reason:
      'campaign-prefix C2 (owner ruling Q-D4-CAMPAIGN-PREFIX, retire): no production path minted it, and `cmp` is the ' +
      'tenant prefix; the residue repair judged the removed rows by this shape',
    appliedFile: 'apps/pos-api/migrations/20261010196840_campaign_prefix_c3c4_test_residue.js',
  }),
});

/**
 * The registered pattern for `entityType`, for every caller that validates or mints LIVE ids.
 *
 * @param {string} entityType - Key from `ID_PREFIXES`.
 * @param {string} caller - Names the caller in the refusal.
 * @returns {RegExp} `^<prefix>_<uuid v4 body>$`
 * @throws {Error} If `entityType` is not registered (a retired type included).
 */
function registeredPrefixPattern(entityType, caller) {
  const prefix = ID_PREFIXES[entityType];
  if (!prefix) {
    throw new Error(`${caller}: unknown entity type "${entityType}". Register in ID_PREFIXES.`);
  }
  return new RegExp(`^${prefix}_${UUID_BODY}$`);
}

function getPrefixPattern(entityType) {
  if (Object.hasOwn(RETIRED_ENTITY_PATTERNS, entityType)) {
    return new RegExp(`^${RETIRED_ENTITY_PATTERNS[entityType].prefix}_${UUID_BODY}$`);
  }
  return registeredPrefixPattern(entityType, 'getPrefixPattern');
}

/**
 * EPI-TYPE-RESTORATION-001 (2026-09-17) — the INVERSE of `ID_PREFIXES`, built once and derived
 * from it, never hand-maintained. The registry is bijective (the `should have no duplicate
 * prefixes` guard test enforces it), so the inversion is total and lossless.
 *
 * @type {Readonly<Object<string, string>>}
 */
const ENTITY_TYPE_BY_PREFIX = Object.freeze(
  Object.fromEntries(
    Object.entries(ID_PREFIXES).map(([entityType, prefix]) => [prefix, entityType]),
  ),
);

/**
 * EPI-TYPE-RESTORATION-001 — which entity type does this 3-char prefix name?
 *
 * NON-THROWING by design. The live-schema conformance contract's whole job is to find ids whose
 * prefix is registered to nothing, so a throw here would be the exception path firing once per row
 * it exists to count.
 *
 * @param {string} prefix - A bare 3-char prefix, e.g. `'dep'`.
 * @returns {string|null} The registered entity type, or `null` when nothing claims the prefix.
 *
 * @example
 *   entityTypeForPrefix('dep'); // 'department'
 *   entityTypeForPrefix('tgt'); // null — retired; nothing mints it
 */
function entityTypeForPrefix(prefix) {
  if (typeof prefix !== 'string') return null;
  return ENTITY_TYPE_BY_PREFIX[prefix] ?? null;
}

/**
 * EPI-TYPE-RESTORATION-001 — read an id's entity type back OFF the id.
 *
 * The registry was write-only until this existed: `generateId` could mint, and `getPrefixPattern`
 * could test an id against ONE expected type, but nothing could answer "what IS this?". That gap
 * is why 12,641,519 `tgt_` ids survived — every validator could say "not a department" and none
 * could say "it is a target, and nothing mints those".
 *
 * NON-THROWING, for the same reason as `entityTypeForPrefix`.
 *
 * @param {string} id - A prefixed entity id.
 * @returns {string|null} The entity type, or `null` when the id is malformed OR its prefix is
 *   registered to nothing.
 *
 * @example
 *   entityTypeOf('dep_550e8400-e29b-41d4-a716-446655440000'); // 'department'
 *   entityTypeOf('tgt_baa7e2aa-3426-484a-b435-ff3e26942da4'); // null
 */
function entityTypeOf(id) {
  if (!isValidPrefixedId(id)) return null;
  return entityTypeForPrefix(id.slice(0, 3));
}

/**
 * EPI-TYPE-RESTORATION-001 — assert an id belongs to the expected entity type, with a diagnostic
 * that names what it ACTUALLY is.
 *
 * The missing half of every refusal in this ecosystem. A hand-rolled `^dep_` Joi pattern answers
 * "fails to match", which reads as a malformed request; it never says "this is a `customer` id" or
 * "this prefix belongs to nothing". Twelve validators across four apps refused the entire
 * production dataset for months while saying only the former.
 *
 * @param {string} id - The id to check.
 * @param {string} entityType - Key from ID_PREFIXES — the type the caller expects.
 * @returns {string} `id`, unchanged, so the call can be inlined at a boundary.
 * @throws {Error} If `entityType` is not registered, `id` is malformed, or `id` is another type.
 *
 * @example
 *   assertEntityId(posDepartmentId, 'department');
 *   // Error: Expected a "department" id (dep_…), but "tgt_baa7e2aa-…" has prefix "tgt", which is
 *   //        not registered to any entity type.
 */
function assertEntityId(id, entityType) {
  const expectedPrefix = ID_PREFIXES[entityType];
  if (!expectedPrefix) {
    throw new Error(
      `assertEntityId: Unknown entity type: "${entityType}". Register in ID_PREFIXES.`,
    );
  }
  if (!isValidPrefixedId(id)) {
    throw new Error(
      `Expected a "${entityType}" id (${expectedPrefix}_…), but ${JSON.stringify(id)} is not a ` +
        `well-formed prefixed id.`,
    );
  }
  const actualPrefix = id.slice(0, 3);
  if (actualPrefix === expectedPrefix) return id;

  const actualType = entityTypeForPrefix(actualPrefix);
  const what = actualType
    ? `is a "${actualType}" id`
    : `prefix "${actualPrefix}" is not registered to any entity type`;
  throw new Error(`Expected a "${entityType}" id (${expectedPrefix}_…), but "${id}" ${what}.`);
}

/**
 * T1-13 (2026-05-14) — build a Joi string schema bound to the entity-type's
 * prefix. Caller passes their own Joi instance so this package does not
 * declare a hard dependency on a specific Joi version (different apps
 * pin different majors). The returned schema is a plain
 * `Joi.string().pattern(...)` so consumers can chain `.required()`,
 * `.allow(null)`, `.label(...)`, etc.
 *
 * @param {string} entityType - Key from ID_PREFIXES.
 * @param {{joi: object, allowNull?: boolean, required?: boolean}} options
 *   - `joi` — Joi instance (the package itself, e.g. `require('joi')`).
 *   - `allowNull` — when true, the schema accepts `null` as well as the prefix-matching string.
 *   - `required` — when true, marks the schema `.required()`.
 * @returns {object} A Joi string schema constrained to the entity-type's prefix.
 *
 * @example
 *   const Joi = require('joi');
 *   const userIdSchema = joiPrefixedId('user', { joi: Joi, required: true });
 *   const schema = Joi.object({ userId: userIdSchema });
 */
function joiPrefixedId(entityType, options = {}) {
  const { joi, allowNull = false, required = false } = options;
  if (!joi || typeof joi.string !== 'function') {
    throw new Error(
      'joiPrefixedId: options.joi is required (pass the Joi instance: `require("joi")`).',
    );
  }
  const pattern = registeredPrefixPattern(entityType, 'joiPrefixedId');
  let schema = joi.string().pattern(pattern);
  if (allowNull) {
    schema = schema.allow(null);
  }
  if (required) {
    schema = schema.required();
  }
  return schema;
}

const {
  slugify,
  disambiguate,
  buildPlanSlug,
  buildLocationSlug,
  buildScheduleDraftSlug,
  buildShiftTemplateSlug,
  buildLaborBudgetSlug,
  MAX_SLUG_LEN,
} = require('./slug');
const { buildPlanName } = require('./planName');
/**
 * The entity types whose ids name NO row (coordinator ruling 2026-10-23): an id minted by `generateId` for state that
 * lives outside the database for minutes. `joiNonRowId({ kind: 'ephemeral', entityType })` admits exactly these, so a
 * request key carrying one declares itself a non-row id with the type's own prefix rule. Keep it to the types that
 * truly are never a row: a saved shipping quote, say, is a `shipping_quotes` row with the same id.
 *
 * @type {ReadonlyArray<string>}
 */
const EPHEMERAL_ID_TYPES = Object.freeze(['challengesession', 'platformCoSignChallenge']);

const { NON_ROW_ID_KINDS, isNonRowIdSchema, joiNonRowId } = require('./nonRowId');
const { SYSTEM_ACTORS, isSystemActor } = require('./systemActors');
const {
  ACTOR_KINDS,
  ACTOR_KIND_VALUES,
  ACTOR_KIND_ID_TYPES,
  kindOfSentinel,
  provenUserActor,
  unprovenUserActor,
  provenCustomerActor,
  provenOwnerActor,
  provenStaffActor,
  unprovenStaffActor,
  auditSubjectOf,
} = require('./actorKinds');

module.exports = {
  generateId,
  SYSTEM_ACTORS,
  isSystemActor,
  // RLT-Q7: the typed audit actor — the kind a seam stamps on its principal.
  ACTOR_KINDS,
  ACTOR_KIND_VALUES,
  ACTOR_KIND_ID_TYPES,
  kindOfSentinel,
  // typed-actor C3: the actor an authentication step proved (or the anonymous requester).
  provenUserActor,
  unprovenUserActor,
  provenCustomerActor,
  provenOwnerActor,
  provenStaffActor,
  unprovenStaffActor,
  auditSubjectOf,
  deterministicId,
  ID_PREFIXES,
  PREFIXED_ID_PATTERN,
  RETIRED_ENTITY_PATTERNS,
  isValidPrefixedId,
  // T1-13 (2026-05-14) — registry-derived per-entity-type pattern helpers.
  getPrefixPattern,
  getIdPrefixPattern,
  buildIdPattern,
  buildTenantPattern,
  TENANT_PREFIX,
  joiPrefixedId,
  // A key that names no row of ours: a provider's id, a registry key, a digest (coordinator ruling 2026-10-21).
  NON_ROW_ID_KINDS,
  isNonRowIdSchema,
  joiNonRowId,
  EPHEMERAL_ID_TYPES,
  // TENANT-ID-001 (EPI-TYPE-RESTORATION-001 Q-12) — see the docblock above `getPrefixPattern`.
  TENANT_ID_PATTERN,
  TEST_TENANT_ID_PATTERN,
  DESIGNATED_TEST_TENANT_ID,
  DESIGNATED_TEST_COMPANY_ID,
  LEAKED_PARTITION_PATTERN,
  TEST_ISOLATION_KEY_PATTERN,
  DISPOSABLE_SCHEMA_PATTERN,
  TENANT_TEMPLATE_SCHEMA,
  isTenantTemplateSchema,
  assertNotTenantTemplateSchema,
  NON_COMPANY_TENANT_SCHEMAS,
  isNonCompanyTenantSchema,
  disposableSchemaPatternSource,
  TENANT_ID_SEMANTICS,
  TENANT_ID_COLUMNS,
  ownTenantColumnMismatches,
  ANY_TENANT_ID_PATTERN,
  TENANT_SURFACE_CLASSES,
  tenantIdPatternFor,
  TENANT_SCHEMA_IDENTIFIER_PATTERN,
  // EPI-TYPE-RESTORATION-001 (2026-09-17) — the INVERSE direction: read an entity type back off an
  // id. `entityTypeForPrefix`/`entityTypeOf` are non-throwing queries (the conformance contract
  // counts unregistered prefixes); `assertEntityId` throws with a diagnostic naming what the id
  // actually is, which is the half every hand-rolled prefix refusal was missing.
  ENTITY_TYPE_BY_PREFIX,
  entityTypeForPrefix,
  entityTypeOf,
  assertEntityId,
  // Slug utilities (Planning Workspace — shared across every app that adds
  // a slug column alongside its raw prefixed-ID primary key).
  slugify,
  disambiguate,
  buildPlanSlug,
  buildLocationSlug,
  // Sibling list-page rollout (2026-04-24) — week-anchored schedule drafts
  // and labor budgets, plus name-anchored shift templates.
  buildScheduleDraftSlug,
  buildShiftTemplateSlug,
  buildLaborBudgetSlug,
  MAX_SLUG_LEN,
  // Canonical sales-plan display name (shared: create wizard auto-name + seed).
  buildPlanName,
};
